import type { TranslationKeys } from "../types/translation";

export const enTranslations: TranslationKeys = {
  // Common
  common: {
    done: "Done",
    loading: "Loading...",
    error: "Error",
    retry: "Retry",
    cancel: "Cancel",
    save: "Save",
    delete: "Delete",
    edit: "Edit",
    add: "Add",
    search: "Search",
    filter: "Filter",
    close: "Close",
    back: "Back",
    next: "Next",
    previous: "Previous",
    continue: "Continue",
    submit: "Submit",
    welcome: "Welcome",
    logout: "Logout",
    login: "Login",
    signup: "Join us",
    register: "Register",
    getStarted: "Get Started",
    settings: "Settings / Billing",
    help: "Help",
    support: "Support",
    admin: "Admin",
    yes: "Yes",
    no: "No",
    ok: "OK",
    apply: "Apply",
    reset: "Reset",
    clear: "Clear",
    all: "All",
    none: "None",
    select: "Select",
    confirm: "Confirm",
    howItWorks: "How it works",
    noDataFound: "No data found",
    tryAdjustingFilters: "Try adjusting your filters",
    somethingWentWrong: "Something went wrong",
    tryAgain: "Try Again",
    store: "Store",
    category: "Category",
    loadingCategories: "Loading categories...",
    allCategories: "Categories",
    noCategoriesFound: "No categories found",
    more: "More",
    searching: "Searching...",
    searchingMessage: "This can take up to 30 seconds...",
    contactUs: "Contact us",
  },

  // Navigation
  nav: {
    products: "Products",
    productSearch: "Search",
    myFavorites: "Saved",
    productTracking: "Product Alerts",
    configuration: "Sourcing Filters",
    helpSupport: "Help and Support",
    discord: "Discord",
    linkDiscord: "Link Discord",
    pennyMealsMemberTitle: "Penny Meals is included with your plan",
    pennyMealsMemberBody: "Cheap dinners from this week's grocery markdowns. Sign in with your DealHawk account.",
    pennyMealsFreeTitle: "Try Penny Meals",
    pennyMealsFreeBody: "Turn grocery markdowns near you into cheap dinners. Included with every DealHawk plan.",
    pennyMealsShort: "Penny Meals",
    pennyMealsSidebarMember: "Included with your plan",
    pennyMealsSidebarFree: "Cheap dinners from local markdowns",
    pennyMealsDismiss: "Hide Penny Meals card",
    chromeExtension: "Chrome Extension",
    menu: "Menu",
    catalog: "Catalog",
    allCategories: "Categories",
    allStores: "Locations",
    viewAllProducts: "View all products",
    lookingForDeals: "Looking for deals",
    store: "Store",
  },

  // Auth
  auth: {
    welcomeBack: "Welcome back",
    signUpMessage: "Don't have an account?",
    loginHere: "Login here",
    signUpHere: "Join us here",
    dontHaveAccount: "First time?",
    alreadyHaveAccount: "Already have an account?",
    email: "Email",
    password: "Password",
    confirmPassword: "Confirm Password",
    name: "Name",
    forgotPassword: "Forgot Password?",
    rememberMe: "Remember Me",
    createAccount: "Create your account",
    joinDealHawk: "Join DealHawk",
    signinButton: "Sign In",
    registerButton: "Register",
    emailPlaceholder: "Enter your email address",
    passwordPlaceholder: "Enter your password",
    namePlaceholder: "Enter your full name",
    confirmPasswordPlaceholder: "Confirm your password",
    verifyEmail: "Verify your email",
    verificationCode: "Verification Code",
    verificationCodeSent: "We've sent a code to your email address",
    checkYourInbox: "Check your inbox",
    weSentACodeTo: "We sent a code to",
    didntGetIt: "Didn't get it?",
    wrongAccount: "Wrong email?",
    signOut: "Sign out",
    backToLogin: "Back to Login",
    didntReceiveCode: "Didn't receive the code?",
    resendCode: "Resend code",
    resendSuccess: "We sent a new code to your email.",
    resendError: "Couldn't send a new code. Please try again in a moment.",
    sending: "Sending...",
    termsAgreement: "By signing in, you agree to our",
    termsOfService: "Terms of Service",
    and: "and",
    privacyPolicy: "Privacy Policy",
    orSignInWith: "or sign in with email",
    continueWithGoogle: "Google",
    continueWithDiscord: "Discord",
    needHelp: "Need help?",
    contactSupport: "Contact Support",
    passwordsDoNotMatch: "Passwords do not match",
    agreeToThe: "I agree to the",
    bySigningUpYouAgree: "By signing up, you agree to our",
    orSignUpWith: "or sign up with",
    // Unified auth flow
    unifiedTitle: "Welcome to DealHawk",
    headline: "Get to the deals first",
    or: "or",
    emailAddressPlaceholder: "Email address",
    newMemberOffer: "New members get 50% off their first month.",
    unifiedSubtitle: "Enter your email to sign in or create an account.",
    continueWithEmail: "Continue with email",
    enterPassword: "Enter your password",
    enterPasswordSubtitle: "Welcome back, {{email}}.",
    createPassword: "Create a password",
    createPasswordSubtitle: "Looks like you're new — let's set you up, {{email}}.",
    minCharacters: "Min 8 characters",
    differentEmail: "Use a different email",
    checking: "Checking...",
    creatingAccount: "Creating account...",
    iAgreeToCreate:
      "I agree to the {{terms}} and {{privacy}} and want to create my DealHawk account.",
    marketingEmailsConsent: "Email me deals at my stores and offers. Unsubscribe anytime.",
  },

  // Landing Page
  landing: {
    // Landing v6: Fetch/Ibotta-style reseller page
    v6NavHow: "How it works",
    v6NavStores: "Stores",
    v6HeroLine1: "Turn clearance",
    v6HeroLine2: "into cash.",
    v6HeroSub: "Find clearance and penny items at stores near you, and see what they resell for.",
    v6StatRating: "App Store rating",
    v6StatPrices: "store prices checked daily",
    v6StatPennies: "penny items flagged",
    v6DealsTitle: "Found this week.",
    v6DealsSub: "Recent finds from the Leads tab in the app.",
    v6FlipsTitle: "What a penny find resells for.",
    v6FlipsSub: "Items flagged as likely pennies at big box stores right now, and what they sold for on eBay.",
    v6SellsFor: "sells for {{price}} on eBay",
    v6SoldCount: "{{count}} sold · reg. {{reg}}",
    v6FlipsNote: "Penny prices aren't guaranteed until they ring up. eBay prices are recent sold medians.",
    v6AltMakitaBattery: "Makita 40V XGT 4.0Ah battery and charger starter pack",
    v6AltRyobiWrench: "RYOBI ONE+ HP 18V compact 3/8 in. impact wrench",
    v6AltMakitaSaw: "Makita 40V XGT brushless reciprocating saw",
    v6AltWeber: "Weber Traveler portable propane grill",
    v6GetStarted: "Get started",
    v6BigBox: "Big box store",
    v6AltBose: "Bose QuietComfort headphones",
    v6AltKitchenAid: "KitchenAid Deluxe 4.5 qt stand mixer",
    v6AltBlackstone: "Blackstone 28 in. 2-burner griddle",
    v6AltMilwaukeeBlower: "Milwaukee M18 FUEL dual battery blower",
    v6MoreRetailersCount: "30+",
    v6MoreRetailers: "more retailers",
    v6AlertsAlt: "DealHawk price alerts: a Hoover at $187 under a $199 target",
    v6FeedAlt: "DealHawk showing Walmart markdowns with price, units left and aisle",
    v6Step1Title: "Pick your stores",
    v6Step1Text: "The ones you already drive past.",
    v6Step2Title: "Get the markdowns",
    v6Step2Text: "Price, units left, aisle, and what it resells for.",
    v6Step3Title: "Flip it, get paid",
    v6Step3Text: "Sell on Amazon, eBay or Marketplace.",
    v6StoresTitle: "Stores we watch",
    v6StoresSub: "Shelf prices, not website prices.",
    v6HaulsTitle: "Members flipping right now",
    v6AltJbl: "JBL speakers a member bought for $99 each",
    v6AltShark: "Shark vacuum a member bought for $89",
    v6AltCoolers: "Nine coolers a member bought at 70% off",
    v6AltMilwaukee: "A cart of Milwaukee items a member bought for a penny each",
    v6Each: "each",
    v6ReviewsTitle: "{{rating}} on the App Store.",
    v6SourceAppStore: "App Store review",
    v6SourceDiscord: "Member post · DealHawk Discord",
    v6PostVacTitle: "$549 vac for $89",
    v6PostPennyTitle: "First penny items",
    v6ReviewAppTr: "A real app, super user friendly! I love it! Deals, here I come!",
    v6ReviewMejorTr: "Your best ally for finding deals and clearance.",
    v6PostVacTr: "Just picked up my $549 vac for $89",
    v6PostPennyTr: "Joined and not even an hour later, my first penny items ever!",
    v6HalfOff: "50% off your first month",
    v6PlansTitle: "One flip pays for it.",
    v6ThenPrice: "first month, then ${{price}}/mo",
    v6PlanStores: "{{stores}} stores + {{nearby}} nearby",
    v6StartPlan: "Start {{plan}}",
    v6FinalLine1: "See today's markdowns",
    v6FinalLine2: "at your stores.",
    v6Disclaimer: "Not affiliated with any retailer shown. Profit is the app's estimate; results vary.",
    // Nav
    navFeatures: "What you get",
    navHauls: "Member finds",
    navPricing: "Pricing",
    navCta: "Log in",
    navStart: "Get started",
    browseFreeDeals: "Browse free online deals",

    // Hero
    promoBadge: "50% off your first month",
    cancelAnytime: "cancel anytime",
    heroBadge: "1,000,000+ markdowns found this week",
    heroLine1: "Know what's marked down",
    heroLine2: "before you shop.",
    heroSubtitle:
      "DealHawk shows the live price, stock, store, and exact aisle before you leave home.",
    heroCtaPrimary: "Find deals near me",
    heroCtaSecondary: "Browse free deals",
    heroPlanNote: "Free online deals · Local plans from $17.50",
    videoProofTitle: "Real hunts. Real shelves.",
    huntAirFryerTitle: "Portable air fryer",
    huntAirFryerDetail: "$50 at Walmart · Aisle I7",
    huntMicrowaveTitle: "Hidden vacuum markdown",
    huntMicrowaveDetail: "$30 at Walmart",
    huntMonitorTitle: "Gaming monitor markdown",
    huntMonitorDetail: "$74 at Walmart",
    huntTvTitle: "TV markdown",
    huntTvDetail: "Found on the shelf",
    huntAppTitle: "DealHawk app",
    huntAppDetail: "The hunt starts here",
    freshFindsTitle: "Fresh finds",
    freshFindsSubtitle: "Top deals first seen in the last 24 hours.",
    freshFindsBrowse: "Browse all deals",
    freshFindsOff: "off",
    freshFindsJustAdded: "Just added",
    freshFindsMinutesAgo: "{{count}} min ago",
    freshFindsHoursAgo: "{{count}} hr ago",
    statSaved: "saved this year",
    statMarkdown: "avg. markdown",
    statStores: "stores",
    retailersLabel: "Finding deals at",

    // What you get
    featuresLabel: "What you get",
    featuresTitle: "Built for the hunt.",
    feature1Title: "Live clearance feed",
    feature1Text:
      "Every in-store markdown at every store near you, updated all day, including prices the website never shows.",
    feature2Title: "Scan & compare",
    feature2Text:
      "Scan any barcode and instantly see price and stock at every store around you, down to the aisle and shelf.",
    feature3Title: "Price-drop alerts",
    feature3Text:
      "Set a target price on anything. Your phone buzzes the second it drops. Never refresh a product page again.",
    feature4Title: "Community picks",
    feature4Text:
      "The hottest finds members are grabbing today, served on a plate, sorted by discount and distance.",

    // How it works
    howItWorksLabel: "How it works",
    howItWorksTitle: "Two minutes to set up. 24 hours to your first find.",
    step1Title: "Pick your stores",
    step1Text:
      "Choose the stores you actually drive past — Walmart, Target, Lowe's, Costco and more.",
    step2Title: "Get pinged",
    step2Text:
      "We watch prices all day and alert you the moment something drops 80%+.",
    step3Title: "Walk in and save",
    step3Text:
      "Aisle-level directions take you straight to the shelf, before the crowd shows up.",

    // Member finds
    memberFindsLabel: "From members across the country",
    memberFindsTitle: "Real receipts. Real markdowns.",
    savedAmount: "Saved ${{amount}}",
    testimonial1:
      "Ninja air fryer was tagged $199. Rang up $38. The app had it before the store even changed the shelf label.",
    testimonial2:
      "Encontré una batidora KitchenAid a $86, precio normal $349. Nunca hubiera pasado por ese pasillo.",
    testimonial3:
      "Two 65-inch TVs at $150 each, off $449. One month of the app paid for itself about forty times over.",
    testimonial4:
      "Compré la aspiradora Shark a $60, y estaba en $299. Mi esposo no lo podía creer cuando vio el recibo.",
    testimonial5:
      "Penny list hit on a Tuesday. $214 of stuff, paid $0.19. Cashier called the manager over to look.",
    testimonial6:
      "Kobalt drill kit for $39 instead of $149. Same shelf I'd walked past twice at full price.",

    // Pricing
    pricingLabel: "Pricing",
    pricingTitle: "One good find covers the month.",
    pricingSubtitle: "50% off your first month on every plan. Cancel anytime.",
    mostPopular: "Most popular",
    perMonth: "/mo",
    firstMonthThen: "First month, then ${{price}}/mo",
    startFindingDeals: "Start finding deals",
    retailerDisclaimer:
      "DealHawk is an independent service and is not affiliated with, endorsed by, or officially connected to any retailer featured on the platform.",
    planBasicDesc: "Perfect for getting started",
    planBasicF1: "1 Walmart + 1 local big-box store, each with 4 extra nearby",
    planBasicF2: "Push notifications & alerts",
    planBasicF3: "Product search & leads",
    planBasicF4: "Mobile app + community access",
    planProDesc: "For serious deal hunters",
    planProF1: "5 Walmart + 5 local big-box stores, plus 15 extra nearby Walmarts and 10 big-box",
    planProF2: "5 Target + 5 Dollar General + 5 Lowe's",
    planProF3: "3 Costco stores + advanced local tools",
    planProF4: "More saved deals + price alerts",
    planProF5: "Priority support",
    planPlusDesc: "More stores, more savings",
    planPlusF1: "3 Walmart + 3 local big-box stores, plus 7 extra nearby of each",
    planPlusF2: "2 Target + Lowe's, Costco & Best Buy",
    planPlusF3: "More saved deals + price alerts",
    planPlusF4: "Everything in Basic included",

    // Landing: receipts, explained
    heroOffBadge: "50% off",
    heroOffRest: "your first month on every plan",
    heroTitleLead: "Your store's in-store clearance,",
    heroTitleHighlight: "live on your phone.",
    heroLede: "Every markdown at the stores near you, with stock and the exact aisle.",
    heroPlatforms: "iPhone, Android, web",
    heroCancel: "Cancel anytime",
    heroAlertLabel: "Price drop · just now",
    heroAlertItem: "Air fryer",
    heroAlertDetail: "Aisle I7 · 5 left",
    heroPhoneAlt: "DealHawk app showing a Frigidaire fridge at $119, 7 units left in aisle E7",
    receiptTitle: "Member receipt",
    receiptItem: "Ninja air fryer",
    receiptTag: "Shelf tag",
    receiptPaid: "Paid",
    receiptSaved: "Saved {{amount}}",
    shelfPricesFrom: "Shelf prices from",
    storesOther: "Other big-box stores",
    storesPenny: "Penny items",
    appTitle: "What you get in the app.",
    appSubtitle: "Stores cut prices in-store and never update the website. We check the shelf.",
    appFeedTitle: "See every markdown",
    appFeedText: "Live clearance at your stores.",
    appScanTitle: "Scan any barcode",
    appScanText: "Scan a barcode or paste a link. See price, stock and aisle at every store near you.",
    appAlertTitle: "Get alerted first",
    appAlertText: "Set a target price. We ping you when it drops.",
    appAisleTitle: "Walk to the aisle",
    appAisleText: "Know the aisle and how many are left.",
    mockFeed1: "Ninja Foodi XL",
    mockFeed2: "Portable air fryer",
    mockFeed3: "Kobalt drill kit",
    mockScanItem: "Gaming monitor",
    mockScanStore: "Store #{{id}}",
    mockScanOnline: "Online",
    mockAlertNow: "DealHawk · now",
    mockAlertTarget: "Shark vacuum hit your target:",
    mockAlertAgo: "DealHawk · 2h ago",
    mockAlertNew: "3 new markdowns at your Target",
    mockAisle: "Aisle I7",
    mockLeft: "5 left",
    mockUpdated: "Updated 2h ago",
    howTitleShort: "Set up in two minutes.",
    howStep1Title: "Pick the stores you drive past",
    howStep1Text: "Walmart, Target, Lowe's, Costco and more.",
    howStep2Title: "We watch shelf prices all day",
    howStep2Text: "You get pinged on drops.",
    howStep3Title: "Walk in and grab it",
    howStep3Text: "Straight to the aisle.",
    findsTitle: "What members paid.",
    findsPrev: "Previous finds",
    findsNext: "More finds",
    findsMemberPhoto: "Member photo",
    findsMember: "DealHawk member",
    findsOff: "{{pct}}% off",
    findTvItem: "65\" TV",
    findTvQuote: "One month of the app paid for itself about forty times over.",
    findMixerItem: "KitchenAid mixer",
    findMixerQuote: "Nunca hubiera pasado por ese pasillo.",
    findVacuumItem: "Shark vacuum",
    findVacuumQuote: "Mi esposo no lo podía creer cuando vio el recibo.",
    findDrillItem: "Kobalt drill kit",
    findDrillQuote: "Same shelf I'd walked past twice at full price.",
    pricingShortSubtitle: "50% off your first month. Cancel anytime.",
    finalLead: "Your next receipt could be",
    finalHighlight: "this good.",

    // FAQ
    faqTitle: "Questions, answered.",
    faqSubtitle:
      "Still unsure? Ask thousands of hunters in our Discord.",
    faq1Q: "How does DealHawk find deals that aren't online?",
    faq1A:
      "We check in-store prices and stock, including local clearance that never shows up online.",
    faq2Q: "Is this only for resellers?",
    faq2A:
      "No. Most members use it to save on things they want. Some resell.",
    faq3Q: "Which retailers are included?",
    faq3A:
      "Walmart, Target, Dollar General, Costco, Best Buy, Lowe's, Amazon, Woot and local hardware stores. More coming.",
    faq4Q: "Can I cancel at any time?",
    faq4A:
      "Yes. No contracts. Cancel from your account anytime.",

    // Final CTA
    ctaLine1: "The deals are already out there.",
    ctaLine2: "Go get them.",
    ctaSubtitle: "50% off your first month · cancel anytime",
    ctaButton: "Start saving with 50% off",

    // Footer
    footerDescription:
      "DealHawk helps you uncover in-store deals, clearance markdowns, and pricing gaps before everyone else.",
    footerPrivacy: "Privacy",
    footerTerms: "Terms",
    footerSupport: "Support",
    footerCopyright: "© {{year}} DealHawk",
    downloadOn: "Download on",
    appStore: "App Store",
    getItOn: "Get it on",
    googlePlay: "Google Play",

    // Landing (Shotbase-style redesign)
    heroCtaOffer: "Get started with 50% off",
    navFreeDeals: "Free deals",
    navPenny: "Penny items",
    heroHaulsBadge: "Real member hauls",
    storesBigBox: "Big Box Retailers",
    appSheetLabel: "The app",
    appSheetTitle: "Find it. Check it. Grab it.",
    appSheetTitleMuted: "No guessing.",
    liveClearanceTag: "Live clearance",
    liveClearanceTitle: "Every markdown at your stores, the minute it hits the shelf",
    memberPaid: "Member paid",
    aisleBadge: "Aisle I7",
    aisleLeft: "5 left",
    heroRegularLabel: "Regular price",
    heroRegularNote: "What the website and most stores still charge.",
    heroShelfLabel: "On the shelf at a store near you",
    heroShelfOff: "75% off",
    heroShelfUnits: "7 units left",
    heroShelfAisle: "Aisle E7",
    heroGapLead: "Same fridge. Same day.",
    heroGapAmount: "$360.99 less",
    heroGapRest: "in the aisle.",
    navCommunity: "Community",
    communityLabel: "Community",
    communityTitle: "The hunt is better with company.",
    communityText: "Every paid plan unlocks our Discord. Members post what they found, where, and what they paid, all day long.",
    discordTag: "Members-only Discord",
    discordMember: "Member",
    discordToday: "Today",
    discordTyping: "Several members are typing…",
    discordCta: "Join with any paid plan",
    appFeedAlt: "DealHawk feed of markdowns at a Walmart store",
    appScanAlt: "DealHawk product search showing price and stock at six nearby stores",
    appAlertAlt: "DealHawk price alerts that reached their target",
    discordCatDeals: "DEALS",
    discordCatHelp: "HELP",
    discordTopic: "Post your hauls. Price, store, photo.",
    discordInput: "Message #member-finds",
    discordPerk1Title: "Members-only channels",
    discordPerk1Text: "Finds, penny items, tips",
    discordPerk2Title: "Alerts by Discord DM",
    discordPerk2Text: "When your target price hits",
    discordPerk3Title: "Help from the team",
    discordPerk3Text: "Open a ticket, 24/7",
    pennyLabel: "Penny items",
    pennyTitle: "Yes, a penny. Members find them every week.",
    pennyText: "Big box retailers ring some clearance up at one cent before it leaves the floor. DealHawk flags penny items at your stores so you get there first.",
    pennyHaul: "Member haul",
    pennyCta: "Find penny items near you",
    pennyVacuums: "15 vacuums at $0.01",
    pennyHoses: "35 boxes of hoses at $0.01",
    pennyLadders: "Ladders at $0.01",
    pennyFirePit: "Fire pit at $0.01",
    pennyStainStriker: "Shark StainStrikers at $0.01",
    pennyPliers: "Plier sets at $0.01",
    pennyMilwaukee: "A cart of Milwaukee at $0.01",
    shelfTitleLead: "Get the deal",
    shelfTitleRest: "before it's gone.",
    shelfText: "DealHawk tracks prices and stock at the stores near you and alerts you the moment something drops, including clearance that never shows up online.",
    appSectionWeb: "Web",
    haulsLabel: "Loved by members",
    haulsTitle: "Real hauls. Real receipts.",
    haulNinjaItem: "Ninja coffee maker",
    haulNinjaQuote: "Someone in my group found this Ninja coffee maker, $249 down to $18, thanks to DealHawk.",
    haulStratosItem: "Shark vacuum",
    haulStratosQuote: "Just picked up my $549 vac for $89.",
    haulGriddleItem: "Blackstone griddle",
    haulGriddleQuote: "Only $51, regular price $227.",
    haulBackpacksItem: "Backpacks",
    haulBackpacksQuote: "Backpacks from $24.97 to just $2.",
    haulWagonItem: "Step2 wagon",
    haulWagonQuote: "$10 down from $80!",
    haulCaroteItem: "Carote cookware",
    haulCaroteQuote: "70% off. I took all 7 they had.",
    haulCoolersItem: "Coolers",
    haulCoolersQuote: "9 coolers at $15, 70% off. Second store.",
    haulSharkItem: "Shark cordless",
    haulSharkQuote: "Tent $35, Shark $69.",
    haulPctOff: "{{pct}}% off",
    haulWas: "was {{price}}",
    faq5Q: "What are penny items?",
    faq5A: "Some big box retailers ring clearance up at $0.01 right before it is pulled from the floor. DealHawk shows penny items at your stores so you can get there first.",
  },

  // Settings
  settings: {
    title: "Settings",
    description: "Manage your account settings and preferences",
    billing: "Billing",
    notifications: "Notifications",
    appearance: "Appearance",
    security: "Security",
    currentPlan: "Current Plan",
    active: "Active",
    upgradeOptions: "Upgrade Options",
    popular: "Popular",
    upgradeToPrefix: "Upgrade to",
    cancelSubscription: "Cancel Subscription",
    cancelSubscriptionDescription:
      "Canceling will end your access to premium features at the end of your current billing period.",
    cancelSubscriptionButton: "Cancel Subscription",
    discordNotifications: "Discord Notifications",
    discordNotificationsDescription:
      "Get deal alerts delivered directly to your Discord account",
    discordLinked: "Discord Account Linked",
    discordLinkedDescription:
      "Your Discord account is connected and you'll receive notifications via direct message",
    discordNotLinked: "Discord Account Not Linked",
    discordNotLinkedDescription:
      "Link your Discord account to receive deal notifications via direct message",
    linkDiscord: "Link Discord Account",
    unlinkDiscord: "Unlink Discord Account",
    linking: "Linking...",
    unlinking: "Unlinking...",
    discordLinkedSuccess: "Discord account linked successfully!",
    discordUnlinkedSuccess: "Discord account unlinked successfully!",
    discordLinkError: "Failed to link Discord account. Please try again.",
    discordUnlinkError: "Failed to unlink Discord account. Please try again.",
    discordUpgradeMessage:
      "Discord notifications are only available for paid plans. Upgrade to link your Discord account and receive deal alerts.",
    viewPlans: "View Plans",
    themeSelection: "Theme Selection",
    themeSelectionDescription:
      "Choose your preferred color theme for the application",
    applying: "Applying...",
    cancelReason: "Reason for cancellation",
    cancelReasonPlaceholder: "Please tell us why you're canceling...",
    keepSubscription: "Keep Subscription",
    language: "Language",
    languageDescription: "Choose your preferred language",
    selectLanguage: "Select Language",
    english: "English",
    spanish: "Spanish",
    loading: "Loading...",
    processing: "Processing...",
    perMonth: "/month",
    discordAccess: "Discord Access",
    store: "Store",
    stores: "Stores",
    allCategories: "All Categories",
    limitedAccess: "Limited Access",
    basicFeatures: "Basic Features",
    noAccess: "No Platform Access",
    upgradeRequired: "Upgrade Required to Use Features",
    upgradeToUnlock: "Upgrade to Unlock Platform Features",
    upgradeToUnlockDescription:
      "Choose a paid plan below to start tracking deals and get Discord notifications.",
    features: "Features",
    themeAppliedSuccess: "Theme applied successfully!",
    themeApplyError: "Failed to apply theme. Please try again.",
    discordUrlUpdatedSuccess: "Discord webhook URL updated successfully!",
    discordUrlUpdateError:
      "Failed to update Discord webhook URL. Please try again.",
    subscriptionCanceledSuccess:
      "Subscription canceled successfully! Your account has been downgraded to free.",
    subscriptionCancelError: "Failed to cancel subscription. Please try again.",
    upgradeError: "Failed to initiate upgrade. Please try again.",
    invalidPlan: "Invalid plan selected",
    checkoutFailed: "Failed to get checkout link",
    manageBilling: "Manage Billing",
    billingPortal: "Billing Portal",
    billingPortalDescription:
      "Manage your subscription, payment methods, and billing history",
    manageBillingButton: "Manage Billing",
    manageBillingError: "Failed to open billing portal. Please try again.",
    plans: {
      free: "Free",
      basic: "Basic",
      starter: "Starter",
      pro: "Pro",
      enterprise: "Enterprise",
    },
    themes: {
      grape: "DealHawk",
      grapeDescription: "Default DealHawk theme with grape and platinum colors",
      neutral: "Neutral",
      neutralDescription: "Clean and professional gray theme",
      orange: "Orange",
      orangeDescription: "Warm and energetic orange theme",
      burgundy: "Burgundy",
      burgundyDescription: "Rich and elegant burgundy theme",
      earthy: "Earthy",
      earthyDescription: "Muted and natural earthy theme",
      darkPurple: "Dark Purple",
      darkPurpleDescription: "Sophisticated dark purple theme",
      highContrast: "High Contrast",
      highContrastDescription: "Bold purple and yellow theme",
    },
    cancelModal: {
      title: "Cancel Subscription",
      subtitle: "Help us improve by telling us why you're leaving",
      reasonLabel: "Reason for cancellation",
      reasonPlaceholder: "Please tell us why you're canceling...",
      keepButton: "Keep Subscription",
      cancelButton: "Cancel Subscription",
      canceling: "Canceling...",
    },
  },

  // Products
  products: {
    noStoresAssigned: "No stores assigned",
    noStoresAssignedMessage:
      "You need to assign stores to view products. Set up your stores in Sourcing Filters to start browsing deals.",
    assignStores: "Assign Stores",
    failedToLoad: "Failed to load products",
    retryLoadMore: "Retry Load More",
    store: "Store",
    category: "Category",
    searchProducts: "Search products...",
    searchPlaceholder: "Search for products...",
    filters: "Filters",
    newestItemsOnly: "Show New Clearance Deals",
    newestItemsDescription: "Show recently discounted items and fresh deals",
    loading: "Loading...",
    loadingProducts: "Loading products...",
    refreshingProducts: "Refreshing products...",
    loadingUserData: "Loading user data...",
    noProductsFound: "No products found",
    noProductsFoundMessage: "Try adjusting your filters or search terms.",
    activelySearching: "We're scanning for deals 24/7! 🎯",
    activelySearchingMessage:
      "Our system continuously monitors inventory across your selected stores. New deals typically appear within 24 hours of activation. Be patient and check back soon!",
    scanningHeadline: "Scanning for deals",
    scanningDescription: "Our system is actively monitoring your selected stores for clearance and discounted inventory. New finds typically surface within 24 hours.",
    scanStep1: "Stores configured",
    scanStep2: "Scanning inventory...",
    scanStep3: "Deals will appear here",
    scanTimeEstimate: "Results usually within 24 hours",
    emptyStateTipsTitle: "While you wait",
    emptyStateTipStores: "Add more stores in Sourcing Filters to increase coverage.",
    emptyStateTipFilters: "Try removing price or category filters for broader results.",
    emptyStateTipSearch: "Clear your search or use more general terms.",
    reviewBotFilters: "Review Sourcing Filters",
    resetFilters: "Reset filters",
    discordSuggestion:
      "Join our Discord community for tips and exclusive deals!",
    waitingPeriod: "New deals appear within 24 hours",
    supportContact: "Contact support if no products appear after 24 hours",
    addToFavorites: "Add to Saved",
    removeFromFavorites: "Remove from Saved",
    hideProduct: "Hide product",
    hideProductNamed: "Hide {{name}}",
    productHidden: "Hidden from {{retailer}}",
    hideProductFailed: "Could not hide this product. Please try again.",
    undoHideProduct: "Undo",
    hiddenProductsTitle: "Hidden products",
    hiddenProductsDescription: "Hidden across all your {{retailer}} stores, including products outside the current feed. The All count shows products still visible. Restore any product whenever you want.",
    hiddenProductsChip: "{{count}} hidden",
    manageHiddenProducts: "Manage hidden products",
    noHiddenProducts: "Nothing hidden",
    noHiddenProductsDescription: "Products you hide from this retailer will appear here.",
    restoreProduct: "Restore",
    restoreAllProducts: "Restore all",
    tabTop: "Top",
    tabAll: "All",
    tabCrazyDeals: "Crazy deals",
    tabPennyDeals: "Penny deals",
    dealFeed: "Deal feed",
    tabDealsA11y: "{{label}} deals, {{count}}",
    tabDealsA11yNoCount: "{{label}} deals",
    moreFilters: "More filters",
    searchTheseDeals: "Search these deals",
    searchFeedA11y: "Search products in this feed",
    clearSearch: "Clear product search",
    hiddenTapToRestore: "Tap a product to show it again. Hidden across all {{retailer}} stores.",
    hiddenRestored: "Restored · back in your feed",
    restoreAllCount: "Restore all {{count}}",
    restoredOne: "1 product restored",
    restoredMany: "{{count}} products restored",
    restoreProductA11y: "Restore {{name}}",
    restoredProductA11y: "{{name}}, restored",
    restoreProductFailed: "Could not restore the product. Please try again.",
    viewDetails: "View Details",
    originalPrice: "Original Price",
    currentPrice: "Current Price",
    discount: "Discount",
    inStock: "In Stock",
    outOfStock: "Out of Stock",
    availability: "Availability",
    // ProductItem specific translations
    noImage: "No Image",
    noUpdates: "No updates",
    daysAgo: "{{days}}d ago",
    hoursAgo: "{{hours}}h ago",
    minutesAgo: "{{minutes}}m ago",
    secondsAgo: "{{seconds}}s ago",
    justNow: "Just now",
    inStorePrice: "In-Store:",
    onlinePrice: "Online:",
    available: "Available:",
    aisle: "Aisle:",
    updating: "Updating...",
    favorited: "Favorited",
    favorite: "Favorite",
    tracking: "Alerts",
    track: "Alert",
    stopping: "Stopping...",
    stopTracking: "Stop Alerts",
    trackingActiveTitle: "Alerts Active",
    trackingActiveDescription:
      "This product is currently being monitored for price alerts.",
    targetPriceLabel: "Target Price (must be less than ${{price}})",
    enterTargetPrice: "Enter target price",
    targetPriceTooHigh: "Target price must be lower than current price",
    viewOnWalmart: "View on Walmart",
    copyUpcTooltip: "Copy UPC to clipboard",
    copySkuTooltip: "Copy SKU to clipboard",
    clickToViewOnWalmart: "Click to view on Walmart.com",
    closeImageModal: "Close image",
    viewBarcode: "View barcode",
    barcode: "Barcode",
    closeBarcodeModal: "Close barcode",
    productBarcode: "Product Barcode",
    viewOtherStores: "View this product in other stores",
    validTargetPriceRequired: "Please enter a valid target price.",
    targetPriceMustBeLower:
      "Target price must be lower than current price (${{price}}).",
    failedToAddTracker: "Failed to add tracker. Please try again.",
    failedToRemoveTracker: "Failed to remove tracker. Please try again.",
    noUpcFound: "No UPC found. Cannot stop tracking.",
    addedToFavorites: "Added to saved",
    removedFromFavorites: "Removed from saved",
    failedToUpdateFavorites: "Failed to update saved",
    freePreviewBadge: "Free Online Access",
    freeSavedDealIncluded: "Full online price history",
    freeOnlinePreview: "Every online feed included",
    freeUnlockLocalDeals: "Unlock Local Deals",
    freeKeepBrowsing: "Keep Browsing",
    freeDismissWelcome: "Dismiss free account welcome",
    freeHintLocalDeals: "Local deals near you",
    freeHintLocalDealsNearZip: "Local deals near {{zip}}",
    freeHintMoreDeals: "Save and organize online deals",
    freeHintMoreDealsCount: "Save and organize online deals",
    freeHintAlerts: "Add paid price alerts",
    freeHintUnlock: "Learn more",
    previewModeUnlockMore: "Every online deal is included free",
    previewModeUnlockMoreCount: "Every {{retailer}} online deal is included free",
    unlockLocalStoreDeals: "Unlock local store deals near your zip",
    unlockMoreDeals: "See Paid Features",
    unlockLocalDeals: "Unlock Local Deals",
    accessRestricted: "Access Restricted",
    accessRestrictedMessage: "You don't have access to {{retailer}} products. Please manage your billing to get access to this retailer.",
    manageBilling: "Manage Billing",
    noMatchingProducts: "No matching products",
    noMatchingProductsMessage: "{{count}} product(s) available, but none match your current filters.",
    noMatchingProductsFiltersMessage: "No products match your current filters. Try clearing them to see more.",
    clearAllFilters: "Clear All Filters",
    findingBestDeals: "Finding the best deals for you...",
    scanningForUpdates: "Scanning for deals nearby",
    refreshProducts: "Refresh products",
    backToTopRefresh: "Back to top & refresh",
    setZipCode: "Set Zip Code",
    setZipCodeDescription: "Enter your zip code to search nearby stores for this product.",
    storeAvailability: "Store Availability",
    swipeToSwitchStores: "Swipe to switch location filters",
    enterCodeToSearch: "Please enter a code to search",
    noResultsForProduct: "No results found for this product",
    failedToSearchProduct: "Failed to search for product",
    nearbyTipsTitle: "Tips & tricks",
    nearbyOnHandTip: "On-hand products can be found on the sales floor or in the backroom.",
    nearbyLowOnHandNoAisleTip: "If the on-hand count is low and no aisle is listed, the item is less likely to be there.",
    salesFloorLabel: "Sales Floor",
    onHandLabel: "- On Hand",
    backroomLabel: "Backroom",
    storeSortBestDeal: "Best deal",
    storeSortLowestPrice: "Lowest price",
    nearbyNoStock: "No nearby stock found",
    nearbyInStockCount: "{{count}} in stock",
    nearbyStoresChecked: "{{count}} stores checked",
    showBarcode: "Show barcode",
    hideBarcode: "Hide barcode",
    storeSortMostOnHand: "Most on hand",
    storeSortClosest: "Closest",
    // Trending banner
    trendingToday: "Trending Today",
    newDealsCount: "{{count}} new deals · avg {{percent}}% off",
    swipe: "swipe",
    // ProductItem badges & labels
    badgeNew: "NEW",
    badgePriceDown: "PRICE ↓",
    badgePriceUp: "PRICE ↑",
    badgeStockUp: "STOCK ↑",
    badgeStockDown: "STOCK ↓",
    soldBy: "Sold by:",
    noLocation: "No Location",
    layout: "Layout",
    pickupShort: "Pickup",
    changedLabel: "Changed:",
    updatedLabel: "Updated:",
    possibleStock: "Possible stock",
    firstSpottedPenny: "Penny since",
    lastSeenPenny: "Last regular",
    nearbyButton: "Check stock",
    nearbyCount: "Nearby ({{count}})",
    moreStores: "+{{count}} more",
    bestLabel: "Best",
    productOptions: "Possible Options",
    optionsTapHint: "Tap any option to find it at nearby stores",
    firstUpdated: "First Updated",
    lastChanged: "Last Changed",
    lastUpdatedLabel: "Last Updated",
    productCode: "Product Code",
    optionsButton: "Options",
    specialClearance: "Special Clearance In Store",
    clearanceInfoTitle: "Clearance Info",
    clearanceInfoWithSavings: "This is the online clearance price. In store you may find up to 70% clearance on this product.",
    clearanceInfoWithoutSavings: "This is the online price. In store you may find up to 70% clearance on this product.",
    gotIt: "Got it",
    pickupAvailable: "Pickup Available",
    openBoxLabel: "Open Box",
    saveAmount: "Save {{amount}}",
    cashbackAmount: "{{amount}} cash back",
    cashbackAmountMinQty: "{{amount}} cash back when you buy {{qty}}",
    ibottaChip: "Ibotta {{amount}} back",
    ibottaChipGeneric: "Ibotta offer",
    ibottaChipAria: "Possible Ibotta offer, open details",
    ibottaModalTitle: "Ibotta offers",
    ibottaAmountBack: "{{amount}} back",
    ibottaExpires: "Expires {{date}}",
    ibottaExpiresSoon: "Expires soon ({{date}})",
    ibottaExpired: "Expired {{date}}",
    ibottaExpirationUnknown: "Expiration unknown",
    ibottaSeen: "Seen on Ibotta {{date}}",
    ibottaPartialMatch: "Partial name match — the offer may cover a different variety.",
    ibottaCaution: "Matched by product name from a saved snapshot. Confirm in the Ibotta app before you buy.",
    ibottaSubtitle: "Possible matches · seen {{date}}",
    ibottaSubtitleNoDate: "Possible matches",
    ibottaSimilar: "similar",
    ibottaShowDetails: "Show details for {{name}}",
    ibottaHideDetails: "Hide details for {{name}}",
    offerBogo: "BOGO",
    offerMixMatch: "Mix & Match",
    regularEach: "Reg. {{amount}} ea",
    unitInDeal: "{{amount}} ea when you buy {{count}}",
    percentOffInDeal: "{{percent}}% off in deal",
    dealPrice: "Deal",
    percentOffLabel: "{{percent}}% off",
    searchStoresButton: "Search Stores",
    enterZipCodeTitle: "Enter Zip Code",
    enterZipCodeDescription: "Enter your zip code to search nearby Home Depot stores for this product.",
    zipCodeLabel: "Zip Code",
    locationAvailabilityTitle: "Location Availability",
    lowestPriceLabel: "Lowest Price",
    inStockCount: "{{count}} in stock",
    lowStock: "Limited stock",
    outLabel: "Out",
    barcodeNotAvailable: "Barcode not available",
    barcodeNotAvailableDesc: "This product doesn't have a scannable barcode",
    gettingBarcode: "Getting barcode...",
    gettingBarcodeWait: "This may take a moment",
    scanBarcodeHint: "Scan this barcode at self-checkout or show to store associate",
    // ProductsHeader
    sortBy: "Sort By",
    lowToHigh: "Low to High",
    highToLow: "High to Low",
    oldestFirst: "Oldest first",
    newestFirst: "Newest first",
    filterByStatus: "Filter By Status",
    allStoresLabel: "All Stores",
    storesChipLabel: "Stores",
    savedKeywordsTitle: "Saved Keywords",
    tapKeywordsToSearch: "Tap keywords to add them to product search.",
    noSavedKeywordsYet: "No saved keywords yet",
    saveSearchReuse: "Save your current search to reuse it across retailers.",
    selectAllKeywords: "Select all keywords",
    clearAllKeywords: "Clear all keywords",
    clearSelected: "Clear selected",
    crazyDeals: "Crazy Deals",
    noCategoriesLabel: "No categories",
    categoriesSheetDescription: "Find what you’re looking for",
    sortStorePrice: "Store Price",
    sortStorePriceDesc: "Current tracked price",
    sortMsrp: "MSRP",
    sortMsrpDesc: "Original listed price",
    sortInventory: "Inventory Count",
    sortInventoryDesc: "Current units on hand",
    sortDiscountAmount: "Discount Amount",
    sortDiscountAmountDesc: "MSRP minus current price",
    sortBestDeal: "Best Deal",
    sortBestDealDesc: "Big savings and % off, mixed across categories",
    sortDiscountPercentage: "Discount Percentage",
    sortDiscountPercentageDesc: "Percent off MSRP",
    sortCashbackAmount: "Cash Back Amount",
    sortCashbackAmountDesc: "Highest cash back first",
    sortOpenBoxDiscount: "Open Box Discount",
    sortOpenBoxDiscountDesc: "Percent off MSRP for open-box price",
    sortChanged: "Changed",
    sortChangedDesc: "Time of the most recent product change.",
    sortUpdated: "Updated",
    sortUpdatedDesc: "Most recent time the product was observed.",
    sortFirstAppeared: "First Appeared",
    sortFirstAppearedDesc: "When product was first updated",
    // Filters modal
    inStore: "In store",
    allSavedStoresDescription: "Browse deals at your saved stores",
    extraStoresHeading: "Extra stores",
    allSavedAndExtraStoresDescription: "Your saved stores + {{count}} extra stores",
    extraStoreBadge: "Extra",
    extraStoreDistance: "{{miles}} mi",
    extraStoreNear: "{{miles}} mi from your {{city}} store (#{{id}})",
    hideExtraStore: "Hide",
    hideExtraStoreA11y: "Hide {{store}}",
    hiddenExtraStoresToggle: "Hidden stores ({{count}})",
    showHiddenExtraStore: "Show",
    showHiddenExtraStoreA11y: "Show {{store}}",
    extraStoreHiddenToast: "Store hidden",
    undoHideExtraStore: "Undo",
    resetAll: "Reset all",
    filtersTitle: "Filters",
    storesTab: "Stores",
    categoriesTab: "Categories",
    priceTab: "Price",
    otherTab: "Other",
    onlineDeals: "Online Deals",
    browseOnlineDeals: "Browse online-only deals",
    selectLocationFilter: "Select Location Filter",
    editLocations: "Edit Locations",
    adminStorePickerTitle: "Admin Store Lookup",
    adminStoreLookupOpen: "Look Up Any Store",
    adminStoreSearchPlaceholder: "Search by store ID, address, or zip...",
    adminStoreActive: "Active custom store: #{{storeId}}",
    adminStoreNoResults: "No stores match your search",
    adminStoreMatchCount: "{{count}} stores",
    adminStoreEmpty: "Start typing to search all stores",
    adminStoreUnknownAddress: "Unknown address",
    adminStoreCatalogCount: "{{count}} stores in catalog",
    oneStoreConfigured: "You only have one store configured",
    searchCategories: "Search categories...",
    allCountSelected: "All {{count}} selected",
    noneSelected: "None selected",
    countOfTotalSelected: "{{count}} of {{total}} selected",
    noCategoriesMatch: "No categories match \"{{search}}\"",
    categoriesSelectedCount: "{{count}} of {{total}} selected",
    selectAllCategories: "Select all",
    clearCategories: "Clear all",
    priceFiltersTitle: "Price Filters",
    storePriceRange: "Store Price Range",
    minimumPrice: "Minimum Price",
    maximumPrice: "Maximum Price",
    noLimit: "No limit",
    msrpRange: "MSRP Range",
    minimumMsrp: "Minimum MSRP",
    maximumMsrp: "Maximum MSRP",
    minimumDiscount: "Minimum Discount",
    discountHint: "Enter discount percentage (e.g., 25 for 25% off)",
    clearPriceFilters: "Clear Price Filters",
    additionalFilters: "Additional Filters",
    variantsOnly: "Variants Only",
    variantsOnlyDesc: "Show only products with variants",
    inStockOnly: "In Stock Only",
    inStockOnlyDesc: "Hide out of stock products",
    hideCashback: "Hide Cashback",
    hideCashbackDesc: "Hide cashback deals under 20% off",
    hotDeals: "Hot Deals",
    hotDealsDesc: "Show only crazy deals with deep discounts",
    pennyItems: "Penny Items",
    pennyItemsDesc: "Show only penny items with extreme markdowns",
    potentialPennyItems: "Penny Items",
    potentialPennyItemsDesc: "Dollar General items that changed to the $0.00 penny marker",
    potentialPennyBadge: "Penny Item",
    pennyItemsProListDisclaimer:
      "Predictions may not reflect in-store availability or price.",
    resetAllFilters: "Reset All Filters",
    // Status labels
    statusAll: "All",
    statusNew: "New",
    statusPriceDrop: "Price Drop",
    statusStock: "Stock",
    statusStockUpdate: "Stock Update",
    statusCoupon: "Coupon",
    statusAisle: "Aisle",
    statusWithOptions: "With Options",
    statusSaved: "Saved",
    statusOpenBox: "Open Box",
    statusPickupYes: "Pickup ✓",
    statusPickupNo: "Pickup ✗",
    statusOnSale: "On Sale",
    statusSoldByWalmart: "Sold by Walmart",
    statusSoldByProSeller: "Sold by Pro Seller",
    statusLowest: "Lowest",
    statusCashback: "Cash Back",
    statusIbotta: "Ibotta",
    statusBogo: "BOGO",
    statusMixMatch: "Mix & Match",
    // Sort chip labels
    sortLabelPrice: "Price",
    sortLabelMsrp: "MSRP",
    sortLabelInventory: "Inventory",
    sortLabelDiscountDollar: "Discount $",
    sortLabelDiscount: "Discount",
    sortLabelCashback: "Cash Back",
    sortLabelOpenBoxDiscount: "Open Box Discount",
    sortLabelChanged: "Changed",
    sortLabelUpdated: "Updated",
    sortLabelNewest: "Newest",
    sortLabelDefault: "Sort",
    // Retailer selector
    retailerLabel: "Retailer",
    inStoreTab: "In Store",
    onlineTab: "Online",
    lockedLabel: "Locked",
    selectRetailer: "Select Retailer",
    feedsLabel: "Feeds",
    hotDealsLabel: "Best Deals",
    hawkFindsLabel: "Hawk Finds",
    premiumLabel: "Premium",
    unlockInStoreAccess: "Unlock In-Store Access",
    upgradeRequired: "Upgrade Required",
    freeUpgradeMessage: "Every online feed is free. Upgrade for exact local store data, more saves, price alerts, and advanced tools.",
    paidUpgradeMessage: "This retailer requires a Pro plan or higher.",
    accessMoreRetailers: "Access more retailers and in-store views",
    unlockFavoritesAlerts: "Unlock more saved deals, alerts, and advanced insights",
    viewPlans: "View Plans",
    hdPlanRequired: "HD Plan Required",
    hdPlanRequiredMessage: "This retailer requires an HD plan.",
    // Settings page
    profileLabel: "Profile",
    profileDescription: "Manage your profile information",
    billingDescription: "Manage your subscription and billing",
    notificationsDescription: "Configure notification preferences",
    supportDescription: "Get help and contact support",
    languageDescription: "Change your language preference",
    backToSettings: "Back to Settings",
    activatingSubscription: "Activating Subscription",
    activatingSubscriptionDesc: "Please wait while we process your payment and set up your account...",
    subscriptionActivated: "Subscription activated successfully! Welcome aboard!",
    failedToProcessPayment: "Failed to process payment. Please contact support.",
    // Profile tab
    subscriptionLabel: "Subscription",
    manageArrow: "Manage →",
    cancellingAtPeriodEnd: "Cancelling at Period End",
    activeLabel: "Active",
    noSubscription: "No Subscription",
    plansLabel: "Plans:",
    accessEnds: "Access Ends:",
    statusLabel: "Status:",
    renewsMonthly: "Renews monthly",
    startSubscriptionHint: "Start a subscription to access premium features",
    nameLabel: "Name",
    emailLabel: "Email",
    notProvided: "Not provided",
    editName: "Edit name",
    namePlaceholder: "Your name",
    nameUpdated: "Name updated.",
    nameUpdateError: "We couldn't update your name.",
    changePhoto: "Change photo",
    removePhoto: "Remove",
    photoUpdated: "Profile photo updated.",
    photoRemoved: "Profile photo removed.",
    photoTooLarge: "Use a JPEG, PNG, or GIF up to 2 MB.",
    photoUpdateError: "We couldn't update that photo.",
    zipCodeSetting: "Zip Code",
    deleteAccount: "Delete Account",
    updateZipCode: "Update Zip Code",
    updateZipCodeDesc: "Enter your zip code to set it as your default location for nearby store searches.",
    // Notifications tab
    notificationsTitle: "Notifications",
    notificationsDesc: "Choose which notifications you want to receive.",
    mobileNotificationsHint: "These control notifications shown in the mobile app. If you have not set preferences yet, both are enabled by default.",
    dailyDealsNotifications: "Daily Deals Notifications",
    dailyDealsNotificationsDesc: "Shows how many new deals have been found twice per day.",
    crazyWootDeals: "Crazy Woot Deals",
    crazyWootDealsDesc: "Get notified when new Woot deals are unusually good.",
    onlineDealNotifications: "Online Deal Alerts",
    onlineDealNotificationsDesc: "Get pinged when we find a great online markdown near you.",
    marketingEmails: "Marketing Emails",
    marketingEmailsDesc: "Deals at your stores, weekly roundups and offers by email.",
    manageEmailPreferences: "Manage email preferences",
    emailOptInCardTitle: "Get your store's weekly clearance list by email",
    emailOptInCardButton: "Turn on",
    emailOptInCardDone: "You're in. Watch your inbox for this week's list.",
    discordNotificationsTitle: "Discord Notifications",
    discordNotificationsDesc: "Get deal alerts through your Discord account.",
    discordConnected: "Discord Connected",
    discordConnectedDesc: "You'll receive deal notifications in Discord",
    discordNotConnected: "Discord Not Connected",
    discordNotConnectedDesc: "Link your Discord account to receive notifications",
    linkDiscordButton: "Link Discord",
    linkingDiscord: "Linking...",
    upgradeRequiredNotif: "Upgrade Required",
    discordPaidPlansOnly: "Discord notifications are available with paid plans",
    notificationPrefUpdated: "Notification preferences updated",
    notificationPrefFailed: "Failed to update notification preferences",
    // BillingTab
    loadingBilling: "Loading billing...",
    freePreview: "Free Account",
    freePreviewUpgradeHint: "Every online feed is included. Upgrade for exact local data, more saves, alerts, and advanced tools.",
    welcomeBack: "Welcome back:",
    limitedTime: "Limited time:",
    halfOffFirstMonth: "50% off first month",
    onlineDealsRow: "Online deals",
    localStoresRow: "Local stores",
    alertsSavesRow: "Alerts & saves",
    productSearchRow: "Product search",
    freeOnlineDeals: "All, live",
    paidOnlineDeals: "Same full feed",
    freeLocalStores: "Teaser",
    paidLocalStores: "Exact, up to 5",
    freeAlertsSaves: "Not included",
    paidAlertsSaves: "More + alerts",
    freeProductSearch: "1/day",
    paidProductSearch: "Unlimited",
    bestValue: "Best Value",
    firstMonth: "first month",
    perMo: "/mo",
    unlockPlan: "Unlock {{name}}",
    cancelAnytimeStripe: "Cancel anytime • Secure payment via Stripe",
    manageYourPlan: "Manage Your Plan",
    upgradeToNewPlans: "Upgrade to our new simplified plans",
    updateSubscription: "Update your subscription",
    scheduledCancellation: "Subscription Scheduled for Cancellation",
    subscriptionEndsIn: "Your subscription will end in {{days}} day(s) on {{date}}",
    reactivate: "Reactivate",
    reactivating: "Reactivating...",
    newPlansAvailable: "New Simplified Plans Available!",
    newPlansDescription: "We've simplified our pricing. Choose one of our new plans below to continue enjoying DealHawk. Your current subscription will be canceled when you switch.",
    currentPlan: "Current Plan",
    renewsOn: "Renews {{date}}",
    discountApplied: "{{percent}}% off applied to next renewal",
    upgradeYourPlan: "Upgrade your plan",
    changeYourPlan: "Change your plan",
    moveTo: "Move to {{name}}",
    upgradeToPro: "Upgrade to Pro",
    downgradeAtPeriodEnd: "Downgrade at Period End",
    downgradeNote: "Downgrading will take effect at the end of your current billing period",
    choosePlanToStart: "Choose a plan to get started",
    paymentMethod: "Payment Method",
    cancelSubscription: "Cancel Subscription",
    waitSpecialOffer: "Wait! Here's a Special Offer",
    getHalfOff: "Get 50% off your next month",
    retentionDescription: "We'd hate to see you go! Accept this one-time discount and keep access to all your deals and alerts.",
    applyingDiscount: "Applying...",
    getHalfOffButton: "Get 50% Off Next Month",
    noThanksCancelAnyway: "No thanks, cancel anyway",
    cancelSubscriptionQuestion: "Cancel Subscription?",
    keepAccessUntilEnd: "You'll keep access until your billing period ends",
    whyAreYouLeaving: "Why are you leaving?",
    tooExpensive: "Too expensive",
    foundBetterAlternative: "Found a better alternative",
    missingFeatures: "Missing features",
    noLongerNeedIt: "No longer need it",
    other: "Other",
    pleaseTellUsMore: "Please tell us more",
    enterYourReason: "Enter your reason...",
    canceling: "Canceling...",
    selectCancelReason: "Please select a reason for cancellation",
    provideDetails: "Please provide details for your cancellation reason",
    failedToCancelSubscription: "Failed to cancel subscription",
    failedToReactivate: "Failed to reactivate subscription",
    failedToChangePlan: "Failed to change plan. Please try again.",
    failedToStartCheckout: "Failed to start checkout. Please try again.",
    failedToMigratePlan: "Failed to start plan migration. Please try again.",
    failedToApplyDiscount: "Failed to apply discount. Please try again.",
    alreadyHaveActivePlan: "You already have an active plan. Please manage billing.",
    pauseInstead: "Pause instead of canceling",
    pauseInsteadHint: "Taking a break? Pause billing instead and pick up where you left off.",
    pauseBillingDescription: "You keep full access until {{accessDate}}. Then billing pauses for {{duration}} and everything resumes automatically on {{date}}. Your stores, alerts and settings stay saved.",
    pauseOneMonth: "1 month",
    pauseMonths: "{{count}} months",
    pauseForDuration: "Pause for {{duration}}",
    pausing: "Pausing...",
    failedToPause: "Failed to pause subscription",
    pauseConfirmTitle: "Subscription paused",
    pauseConfirmBody: "You keep full access until {{accessDate}}. After that, billing pauses and everything resumes automatically on {{date}}. Your stores, alerts and settings are saved, and you can resume earlier from Billing.",
    subscriptionPausedUntil: "Subscription paused until {{date}}",
    subscriptionPausedNote: "Deal alerts are off while paused. Your stores and settings are saved.",
    resumeNow: "Resume now",
    resuming: "Resuming...",
    subscriptionResumed: "Subscription resumed",
    failedToResume: "Failed to resume subscription",
    pauseManagedInAppStore: "Pausing is managed in your app store: Google Play supports pausing in Play Store › Subscriptions; Apple App Store does not support pausing — you can turn off auto-renew instead.",
    takingABreak: "Taking a break",
    dealsWerentAccurate: "Deals weren't accurate",
    cancelStepDownTitle: "A smaller plan may fit better",
    cancelStepDownBody: "Switch to {{plan}} for {{price}}/month. You keep your current plan until this billing period ends, then the lower price starts.",
    cancelStepDownButton: "Switch to {{plan}}",
    cancelStepDownScheduled: "Done. You'll move to {{plan}} when this billing period ends.",
    cancelSetupSavedNote: "Your stores and preferences stay saved. If you continue, we'll remind you before access ends so you can reactivate without rebuilding your setup.",
    cancelWhichFeature: "Which feature did you need?",
    cancelWhatWorkedBetter: "What worked better for you?",
    cancelWhichDealWasOff: "Which store or deal was off?",
    cancelWhichDealWasOffPlaceholder: "e.g. Walmart #1234, the price on the shelf was different",
    cancelInaccurateThanks: "Thanks for telling us. Your report goes straight to our team so we can fix it.",
    optionalLabel: "(optional)",
    subscriptionPauseScheduled: "Pause starts {{accessDate}}. You have full access until then; billing resumes {{date}}.",
    // AppearanceTab
    darkMode: "Dark Mode",
    darkThemeActive: "Dark theme active",
    lightThemeActive: "Light theme active",
    colorTheme: "Color Theme",
    chooseAccentColors: "Choose your preferred accent colors",
    applyingTheme: "Applying...",
    // StripePaymentModal
    completeYourPurchase: "Complete Your Purchase",
    payNowToGetAccess: "Pay now to get access",
    settingUpPayment: "Setting up your payment...",
    paymentSuccessful: "Payment Successful!",
    subscriptionActivatedSuccess: "Your subscription has been activated successfully.",
    redirectingNow: "Redirecting you now...",
    processingPayment: "Processing your payment...",
    pleaseWaitMoment: "Please wait, this may take a moment",
    failedToLoadPaymentForm: "Failed to load payment form. Please try again.",
    // StripePaymentForm
    loadingPaymentForm: "Loading payment form...",
    completePurchase: "Complete Purchase",
    processing: "Processing...",
    paymentDisclaimer: "A temporary $1 authorization will be placed on your card to verify it and will be released immediately. You will be charged starting today.",
    securedByStripe: "Secured by Stripe - PCI DSS Level 1 Compliant",
    // PlanItem
    planLabel: "{{name}} Plan",
    currentBadge: "Current",
    storeLabel: "{{count}} Store",
    storesLabel: "{{count}} Stores",
    noRetailersLoading: "No retailers available. Loading...",
    // LeadsPage
    errorLoadingLeads: "Error Loading Leads",
    searchLeads: "Search leads...",
    searchLeadsCount: "Search {{count}} leads",
    deals: "deals",
    allCategories: "All Categories",
    saved: "Saved",
    noLeadsFound: "No leads found",
    noLeadsFoundHint: "Try adjusting your search or filters",
    categoryLabel: "Category",
    editLead: "Edit Lead",
    freeLeadsBanner: "Free preview: these leads are delayed 7+ days",
    freeLeadsBannerSub: "Upgrade for the newest leads the day they drop, plus saves and alerts you can act on faster.",
    freeLeadsUnlock: "Unlock Fresh Leads",
    freeLeadsPreviewHint: "Preview mode: free leads show older opportunities so you can judge the quality before paying for the live feed.",
    percentOff: "{{percent}}% off",
    storeAvailabilityLabel: "Store Availability",
    setZipCodeTitle: "Set Zip Code",
    setZipCodeDesc: "Enter your zip code to search nearby stores for this product.",
    leadDeleted: "Lead deleted",
    leadUpdated: "Lead updated",
    failedToDeleteLead: "Failed to delete lead",
    failedToUpdateLead: "Failed to update lead",
    savingLabel: "Saving...",
    // TracksPageNew
    priceAlerts: "Price Alerts",
    alertsUsed: "{{used}} of {{limit}} alerts used",
    readyCount: "{{count}} ready",
    filterAll: "All",
    filterReady: "Ready",
    filterWatching: "Watching",
    noAlertsYet: "No alerts yet",
    noAlertsYetDesc: "Track products from search results to get notified when prices drop.",
    configureNotificationsIn: "Configure notifications in",
    noAlertsReady: "No alerts have hit target yet",
    noAlertsWatching: "No alerts still watching",
    viewAll: "View all",
    failedToLoadAlerts: "Failed to load alerts",
    tryAgainAction: "Try again",
    storesInStock: "{{count}} store(s) in stock",
    outOfStockLabel: "Out of stock",
    pickupChecking: "Checking pickup…",
    pickupUnverified: "Pickup not verified",
    priceChecking: "Checking price…",
    priceUnverified: "Price not verified",
    possiblePenny: "Possible penny",
    possiblePennyDescription: "This store has it on clearance but Home Depot is hiding the price, which is how penny items show up. Verify at the register.",
    inStoreLabel: "in-store",
    targetLabel: "target",
    refreshLabel: "Refresh",
    deleteLabel: "Delete",
    storeNumber: "Store #{{id}}",
    floorLabel: "Floor:",
    backLabel: "Back:",
    copiedLabel: "Copied",
    zipCodeRequired: "Zip Code Required",
    zipCodeRequiredDesc: "A zip code is required to update tracker pricing. Please enter your zip code to continue.",
    setZipCodeToast: "Please set your zip code to update this tracker",
  },

  // Product card / gallery / nearby (shared card strings)
  card: {
    upc: "UPC",
    sku: "SKU",
    tcin: "TCIN",
    itemId: "Item ID",
    copyCode: "Copy {{code}}",
    viewBarcode: "View Barcode",
    shareLinkCopied: "Share link copied!",
    shareFailed: "Failed to share deal",
    shareLinkFailed: "Failed to create share link",
    hdSearchFailed: "Failed to search HD stores",
    invalidZip: "Please enter a valid zip code",
    copiedToClipboard: "{{label}} copied to clipboard",
    msrp: "MSRP:",
    lowest: "LOWEST",
    openBoxPrice: "Open Box {{price}}",
    openBoxPriceOff: "Open Box {{price}} ({{percent}}% Off)",
    openBoxCondition: "Open Box · {{condition}}",
    openBoxConditionPriceOff: "Open Box · {{condition}} {{price}} ({{percent}}% Off)",
    openBoxExcellent: "Excellent",
    openBoxGood: "Good",
    openBoxFair: "Fair",
    openBoxAlso: "Also open-box here: {{units}}",
    optionCount: "{{count}} option",
    optionsCount: "{{count}} options",
    collapseDetails: "Collapse details",
    expandDetails: "Expand details",
    share: "Share",
    unavailable: "Unavailable",
    warehouseNumber: "Warehouse #{{id}}",
    storeId: "Store {{id}}",
    verdictBuyNow: "🔥 BUY NOW",
    verdictGoodDeal: "✓ Good Deal",
    verdictWait: "⏳ Wait",
    verdictOverpriced: "⚠️ Overpriced",
    score: "Score:",
    low: "Low:",
    high: "High:",
    daysTracked: "{{count}}d tracked",
    zipPlaceholder: "Enter zip code (e.g., 90210)",
    unknown: "Unknown",
    clearanceInStore: "Clearance In Store",
    hot: "Hot",
    goodDeal: "Good Deal",
    availCount: "{{count}} Avail",
    viewDetails: "View Details →",
    saveChoiceFailed: "Could not save your choice. Please try again.",
    browseOnlineDeals: "Browse online deals",
    resumeSavingDeal: "Resume saving deal",
    resumePriceHistory: "Resume price history",
    amazonPrice: "Amazon: {{price}}",
    prime: "Prime",
    discord: "Discord",
    bestDeal: "BEST DEAL",
    onClearance: "On clearance",
    percentOffUpper: "{{percent}}% OFF",
    openSavedKeywords: "Open saved keywords",
    saveKeyword: "Save \"{{term}}\"",
    deleteKeyword: "Delete {{keyword}}",
    listView: "List view",
    galleryView: "Gallery view",
    listShort: "List",
    gridShort: "Grid",
    moreTab: "More",
    clearCategorySearch: "Clear category search",
    dealStatus: "Deal status",
  },

  // Product Search
  productSearch: {
    title: "Product Search",
    description:
      "Search for product information by UPC or SKU across all your stores",
    searchType: "Search Type",
    upc: "UPC",
    sku: "SKU",
    url: "URL",
    qr: "QR",
    searchButton: "Search",
    searchResults: "Search Results",
    noResults: "No Results",
    noResultsMessage: "No products found with the provided search criteria.",
    productFound: "Product Found",
    productInfo: "Product Information",
    price: "Price",
    category: "Category",
    brand: "Brand",
    productDescription: "Description",
    availability: "Availability",
    storeInfo: "Store Information",
    // URL search
    walmartUrl: "Retailer URL",
    urlPlaceholder:
      "Paste product URL (e.g., https://www.walmart.com/ip/product-name/1234567890 or https://www.homedepot.com/p/product-name/123456789)",
    urlHelpText:
      "Supported: Walmart and HD product URLs. The SKU/Product ID will be automatically extracted from the URL.",
    urlParseError: "Only Walmart and HD URLs are supported",
    invalidUrlFormat: "Invalid URL format",
    invalidSkuFormat: "Invalid SKU/Product ID format in URL",
    failedToParseUrl: "Failed to parse URL:",
    // QR search
    qrCode: "QR Code",
    qrHelpText: "Scan Walmart QR codes to automatically search for products.",
    qrScanned: "QR code scanned! Processing...",
    qrProcessing: "Processing QR code...",
    qrExtracting: "Extracting product information",
    scanQrInstruction: "Scan Walmart QR code to search",
    scanQrButton: "Scan QR Code",
    failedToProcessQr: "Failed to process QR code",
    // Camera/Scanner
    scanBarcodeTitle: "Scan barcode with camera",
    scanning: "Scanning...",
    scan: "Scan",
    scanQrTitle: "Scan QR Code",
    scanBarcodeModalTitle: "Scan Barcode",
    closeScanner: "Close scanner",
    positionQrCode: "Position the QR code within the frame",
    positionBarcode: "Position the barcode within the frame",
    cameraPlaceholder: "Camera will appear here when scanning starts",
    barcodeScannedSuccess: "Barcode scanned:",
    // Product details
    skuLabel: "SKU:",
    upcLabel: "UPC:",
    msrpLabel: "MSRP:",
    categoryLabel: "Category:",
    onlinePriceLabel: "Online Price:",
    productBarcode: "Product Barcode",
    clickToViewLarger: "Click to view larger",
    closeBarcodeView: "Close barcode view",
    // Action buttons
    trackProduct: "Set Alert",
    stopTracking: "Stop Alerts",
    trackProductMobile: "Alert",
    stopTrackingMobile: "Stop Alert",
    processing: "Processing...",
    addFavorite: "Add Saved",
    removeFavorite: "Remove Saved",
    addFavoriteMobile: "Save",
    removeFavoriteMobile: "Remove",
    addToDb: "Add to DB",
    addToDbMobile: "Add DB",
    adding: "Adding...",
    trackingActive: "✓ Alert Active",
    // Tooltips
    trackProductTooltip: "Set alert for this product for price changes",
    stopTrackingTooltip: "Stop alerts for this product",
    addFavoriteTooltip: "Add to saved",
    removeFavoriteTooltip: "Remove from saved",
    addToDbTooltip: "Add this product to database",
    // Store availability
    storeAvailabilityTitle: "Store Availability & Pricing",
    storePrice: "Store Price",
    stock: "Stock",
    location: "Location",
    inStock: "In Stock",
    outOfStock: "Out of Stock",
    notSpecified: "Not specified",
    percentOff: "% OFF",
    // Results and errors
    productNotFound: "Product Not Found",
    allStoresFailed: "All stores could not be searched for this product.",
    someStoresFailed:
      "Some stores could not be searched. This may be due to network issues or the product not being available in those locations.",
    outOfStockHidden: "store(s) with out of stock items are hidden.",
    // User messages
    noStoresConfigured:
      "You don't have any stores configured. Please set up your stores in the",
    setupPage: "setup page",
    toUseProductSearch: "to use product search.",
    // Alerts and notifications
    pleaseLoginFavorites: "Please log in to add to saved.",
    noProductUpcAvailable: "No product UPC available to add to saved.",
    productAddedToFavorites: "Product added to saved!",
    productRemovedFromFavorites: "Product removed from saved.",
    failedToUpdateFavorites: "Failed to update saved.",
    trackingFunctionalityDisabled:
      "Alerts functionality is being updated for multi-store support. Please check back soon!",
    adminFunctionalityDisabled:
      "Admin add to database functionality is being updated for multi-store support. Please check back soon!",
  },

  // Top feed (TopProductsView)
  topFeed: {
    todaysTopDeals: "Today's top deals",
    bestDealsNow: "Best deals right now",
    freshSubtitle: "{{count}} fresh · avg {{pct}}% off",
    bestMarkdownsNow: "Best markdowns available right now",
    justDropped: "Just dropped",
    moreTopPicks: "More top picks",
    hot: "HOT",
    nearby: "Nearby",
    percentOffShort: "{{pct}}% off",
    storeFallback: "Store {{id}}",
    freshOnlineDeals: "Fresh online deals",
    addedInLastHours: "{{count}} added in the last {{hours}}h",
    strongMarkdowns: "Strong online markdowns available now",
    seeAllNew: "See all new",
    topCategory: "Top {{category}}",
    railSubtitle: "{{count}} deals · avg {{pct}}% off",
    seeAll: "See all",
    browseAllDeals: "Browse all {{count}} deals",
    online: "Online",
    onlineSoldBy: "Online · Sold by {{seller}}",
    left: "{{count}} left",
    lowStock: "Limited stock",
    checkStock: "Check stock",
    inStock: "In stock",
    aisle: "Aisle {{aisle}}",
    updated: "Updated {{time}}",
    youSave: "You save",
    discount: "Discount",
    dayLow: "{{days}}-day low",
    yes: "Yes",
    lowestSeen: "Lowest seen",
    listed: "Listed",
    today: "Today",
    topDealToday: "Top deal today",
    upgradeToSaveDeals: "Upgrade to save deals",
    realWins: "Real wins from DealHawk members",
    viewDeal: "View deal",
    loadingCommunity: "Loading Community Success",
    communitySuccess: "Community success",
    communitySubtitle: "Real finds from DealHawk members",
    communityDealFind: "Community deal find",
    communityFree: "Community wins are free to browse",
    communityFreeSub: "See what members are finding in stores right now.",
    freeToBrowse: "Free to browse",
    upgradeToShare: "Upgrade to share your wins",
    justNow: "just now",
    minutesAgo: "{{count}}m ago",
    hoursAgo: "{{count}}h ago",
    daysAgo: "{{count}}d ago",
    badgeLowest: "LOWEST",
    badgeNew: "NEW",
    percentOff: "{{pct}}% OFF",
  },

  // Leads (TopLeadsView, LeadsPage, LeadSaveButton)
  leads: {
    badgeJustNow: "just now",
    badgeMinutes: "{{count}}m",
    badgeHours: "{{count}}h",
    badgeDays: "{{count}}d",
    todaysTopLeads: "Today's top leads",
    bestLeadsNow: "Best leads right now",
    freshSubtitle: "{{count}} fresh · avg {{pct}}% off",
    bestMarkdownsNow: "Best markdowns available right now",
    seeAllCount: "See all {{count}}",
    moreOnTheWay: "More top leads are on the way",
    browseAllHint: "Browse all leads to see every available deal.",
    topCategory: "Top {{category}}",
    seeAll: "See all",
    moreTopPicks: "More top picks",
    justDropped: "Just dropped",
    newestLeadsFirst: "Newest leads first",
    browseAllLeadsCount: "Browse all {{count}} leads",
    browseAllLeads: "Browse all leads",
    previousProducts: "Previous {{title}} products",
    nextProducts: "Next {{title}} products",
    railProducts: "{{title}} products",
    percentOff: "{{pct}}% OFF",
    visit: "Visit",
    topLeads: "Top leads",
    topLeadsShort: "Top",
    allLeads: "All leads",
    allLeadsShort: "All",
    leadsView: "Leads view",
    aboutTitle: "What are Leads?",
    aboutBody: "Curated clearance finds from stores nationwide. Tap {{action}} to see the price and stock at stores near you.",
    aboutDismiss: "Dismiss Leads explanation",
    admin: "Admin",
    manageCategories: "Manage categories",
    manageCategoriesHint: "Rename labels with the pencil or use the arrows to arrange categories.",
    closeCategoryManager: "Close category manager",
    noCustomCategories: "No custom categories yet",
    noCustomCategoriesHint: "Create one by importing products from a Discord channel.",
    editLabelNamed: "Edit {{name}} label",
    editLabel: "Edit label",
    moveUpNamed: "Move {{name}} up",
    moveUp: "Move up",
    moveDownNamed: "Move {{name}} down",
    moveDown: "Move down",
    deleteCategoryNamed: "Delete {{name}} category",
    deleteCategory: "Delete category",
    newCategory: "New category",
    saveOrder: "Save order",
    confirmDeleteLead: "Delete \"{{title}}\"?",
    optionsWalmartOnly: "Options lookup is only supported for Walmart leads",
    foundOptions: "Found {{count}} options",
    noOptionsFound: "No options found",
    failedFindOptions: "Failed to find options",
    confirmDeleteCategory: "Delete the “{{name}}” category?\n\nIts leads will stay available under All Leads.",
    categoryDeleted: "“{{name}}” category deleted",
    failedDeleteCategory: "Failed to delete category",
    editCategoryLabelPrompt: "Edit category label",
    categoryLabelChanged: "Category label changed to “{{name}}”",
    failedUpdateCategoryLabel: "Failed to update category label",
    categoryOrderSaved: "Category order saved",
    failedSaveCategoryOrder: "Failed to save category order",
    allCategories: "All categories",
    categoriesCount: "{{count}} categories",
    copied: "{{label}} copied",
    link: "Link",
    failedSearchProduct: "Failed to search for product",
    failedToLoad: "Failed to load",
    refreshLeads: "Refresh leads",
    createCategory: "Create category",
    createLeadCategory: "Create lead category",
    chooseCategory: "Choose category",
    savedLeads: "Saved leads",
    sortLeads: "Sort leads",
    savings: "Savings",
    newest: "Newest",
    hideSearch: "Hide search",
    showSearch: "Show search",
    searchLeads: "Search leads",
    clearSearch: "Clear search",
    leadsCount: "{{count}} leads",
    chooseCategories: "Choose categories",
    clear: "Clear",
    clearCategories: "Clear categories",
    manage: "Manage",
    findCategory: "Find a category",
    applyCategories: "Apply categories",
    showLeadsCount: "Show {{count}} leads",
    newestFirst: "Newest first",
    biggestSavings: "Biggest savings",
    moreActions: "More actions",
    visitSite: "Visit {{site}}",
    opensProductPage: "Opens the product page",
    copySku: "Copy SKU",
    copyLabel: "Copy {{label}}",
    share: "Share",
    unavailable: "Unavailable",
    checkNearby: "Check stock",
    groupLastHour: "Last hour",
    groupToday: "Earlier today",
    groupYesterday: "Yesterday",
    groupThisWeek: "This week",
    groupOlder: "Older",
    tierHuge: "80% off or more",
    tierBig: "50–79% off",
    tierSmall: "Up to 49% off",
    tierNone: "Other finds",
    optionsCount: "{{count}} options",
    adminEditLead: "Edit lead",
    adminFindOptions: "Find options",
    adminDeleteLead: "Delete lead",
    layoutLabel: "Layout",
    layoutGrid: "Grid view",
    layoutList: "List view",
    more: "More",
    fieldTitle: "Title",
    fieldUpc: "UPC",
    fieldSku: "SKU",
    fieldCategory: "Category",
    fieldLowestPrice: "Lowest Price",
    fieldMsrp: "MSRP",
    fieldOnlinePrice: "Online Price",
    fieldImageUrl: "Image URL",
    relJustNow: "Just now",
    relMinutesAgo: "{{count}}m ago",
    relHoursAgo: "{{count}}h ago",
    relDaysAgo: "{{count}}d ago",
    removeFromSaved: "Remove from saved",
    addToSaved: "Add to saved",
    removing: "Removing...",
    adding: "Adding...",
    saved: "Saved",
    save: "Save",
  },

  // Admin
  admin: {
    title: "Admin Panel",
    description: "Manage your DealHawk platform",
    users: "Users",
    categories: "Categories",
    manageUsers: "Manage Users",
    manageCategories: "Manage Categories",
    addUser: "Add User",
    editUser: "Edit User",
    deleteUser: "Delete User",
    addCategory: "Add Category",
    editCategory: "Edit Category",
    deleteCategory: "Delete Category",
    userCreated: "User created successfully",
    userUpdated: "User updated successfully",
    userDeleted: "User deleted successfully",
    categoryCreated: "Category created successfully",
    categoryUpdated: "Category updated successfully",
    categoryDeleted: "Category deleted successfully",
  },

  // Product search page
  searchPage: {
    title: "Find it in store",
    subtitle: "Price and stock for any item at your {{retailer}} stores.",
    lockedOnFree: "Product search is locked on Free. Upgrade to Basic or Pro.",
    noResultsForTracking: "No search results available for tracking",
    trackerAdded: "Tracker added successfully!",
    trackerAddFailed: "Failed to add tracker",
    enterCode: "Please enter a UPC, SKU, or product URL",
    enterZip: "Please enter your zip code",
    enterZipToSearch: "Please enter your zip code to search",
    unableToDetectCode: "Unable to detect code type. Please check your input.",
    searchFailed: "Failed to search for product",
    missingCodeOrZip: "Missing search code or zip code",
    cameraAccessDenied: "Camera access denied",
    cameraError: "Camera error: {{message}}",
    photoDecodeFailed: "Failed to read barcode from photo",
    noResultsForDiscord: "No successful product results to send to Discord",
    noResultsForLeads: "No successful product results to add to leads",
    shareLinkCopied: "Share link copied!",
    shareLinkFailed: "Failed to create share link",
    historyLoadFailed: "Failed to load search history",
    providersLoadFailed: "Failed to load search providers",
    placeholderHomeDepot: "Enter ItemId or HD URL",
    placeholderDollarGeneral: "Enter UPC or Dollar General URL",
    placeholderTarget: "Enter TCIN (Target SKU)",
    placeholderBestBuy: "Enter Best Buy SKU",
    placeholderCostco: "Enter Costco Item Number",
    placeholderTractorSupply: "Enter Tractor Supply item number, SKU, UPC, or URL",
    placeholderSamsClub: "Enter UPC or Sam's Club item #",
    placeholderAmazon: "Enter Amazon ASIN",
    placeholderWalmart: "Enter UPC, SKU, or Walmart URL",
    clearSearch: "Clear search",
    detected: "Detected {{type}}",
    scanBarcode: "Scan barcode",
    uploadBarcodePhoto: "Upload barcode photo",
    photo: "Photo",
    setZipCode: "Set zip code",
    setZip: "Set zip",
    zipPlaceholder: "Enter your zip code",
    scope: "Scope",
    myStores: "My stores",
    nationwide: "Nationwide",
    adminOnly: "Admin only",
    dataSource: "Data source",
    userOverride: "User override ({{provider}})",
    defaultProvider: "Default (from config)",
    providerDisabled: "disabled",
    bypassCache: "Bypass cache",
    recent: "Recent",
    noSearchesYet: "No searches yet",
    noSearchesYetHint: "Your product searches will appear here",
    stockOf: "{{inStock}} of {{total}} in stock",
    outOfStock: "Out of stock",
    noResults: "No results",
    unknownProduct: "Unknown Product",
    viewResultsFor: "View results for {{name}}",
    searchAgainFor: "Search again for {{name}}",
    noMoreResults: "No more results",
    liveScanner: "Live Scanner",
    scannerHintQr: "Hold the code inside the frame until it resolves.",
    scannerHintBarcode: "Fill the frame with the barcode for the fastest read.",
    cameraReady: "Camera ready",
    initializingCamera: "Initializing camera",
    squareFocus: "Square focus",
    horizontalBarcodeFocus: "Horizontal barcode focus",
    cameraErrorTitle: "Camera Error",
    guidance: "Guidance",
    guidanceQr: "Center the QR code in the square guide and hold steady for a moment.",
    guidanceBarcode: "Line the barcode up across the scan beam and avoid cutting off the edges.",
    scanningLive: "Scanning live",
    preparingScanner: "Preparing scanner",
    hdNationwideSearching: "Scanning all Home Depot stores — this may take about 2 minutes.",
    clientSearchTime: "Client search time:",
    durationMs: "{{ms}} ms",
    durationS: "{{s}} s",
    serviceMaintenance: "Service Maintenance",
    searchError: "Search Error",
    unlockLocalSearch: "Unlock Local Search",
    unableToFindAtStores: "Unable to find at any stores",
    notFound: "Not found",
    itemLabel: "Item",
    bestStore: "Best Store",
    online: "Online",
    share: "Share",
    lead: "Lead",
    admin: "Admin",
    storeCount: "{{count}} store",
    storeCountPlural: "{{count}} stores",
    outOfStockCount: "{{count}} out of stock",
    clearance: "Clearance",
    addressCopied: "Address copied",
    copyFailed: "Failed to copy",
    clickToCopy: "Click to copy",
  },

  // Alerts
  alerts: {
    justNow: "now",
    minutesAgo: "{{m}}m ago",
    hoursAgo: "{{h}}h ago",
    daysAgo: "{{d}}d ago",
    stopTrackingConfirm: "Stop tracking this item?",
    removeFailed: "Failed",
    alreadyTracked: "You already have an alert for this product.",
    trackerAdded: "Tracker added successfully!",
    trackerAddFailed: "Failed to add tracker",
    adding: "Adding...",
    tracking: "Tracking",
    alert: "Alert",
    upgradeTitle: "Turn promising deals into instant alerts",
    upgradeDescription: "Track {{productName}} and get notified when price or stock changes instead of checking it manually.",
    upgradeHighlightNotify: "Get alerted when the price or stock changes",
    upgradeHighlightMonitor: "Monitor deals you already validated in the preview",
    upgradeHighlightLocal: "Unlock local store visibility and more live results",
    unlockAlerts: "Unlock Alerts",
    limitTitle: "Alert Limit Reached",
    limitSubtitle: "You've hit the maximum",
    usageLabel: "Your Alert Usage",
    noSlotsAvailable: "0 slots available",
    limitUsingAll: "You're using all",
    limitPriceAlerts: "{{limit}} price alerts",
    limitRemoveFirst: "To add a new alert, you'll need to remove an existing one first.",
    tipLabel: "💡 Tip:",
    tipText: "Review your alerts and remove ones for products you've already purchased or are no longer interested in.",
    manageMyAlerts: "Manage My Alerts",
  },

  // Help
  help: {
    title: "Help & Support",
    description: "Get the help you need from our support team",
    contactSupport: "Contact Support",
    contactSupportDescription:
      "Need help? Join our Discord server for instant support from our community and team.",
    joinDiscord: "Join Discord",
    joinDiscordDescription:
      "Get real-time support and connect with other DealHawk users",
    howToGetSupport: "How to Get Support",
    joinDiscordServer: "Join our Discord Server",
    joinDiscordServerDescription:
      "Click the button below to join our Discord community where you can get real-time help.",
    createSupportTicket: "Create a Support Ticket",
    createSupportTicketDescription:
      "In the Discord server, use the support channels to create a ticket for personalized assistance.",
    getHelpFromTeam: "Get Help from our Team",
    getHelpFromTeamDescription:
      "Our support team will respond to your ticket and help resolve your issue quickly.",
    joinDiscordServerButton: "Join Discord Server",
    needImmediateAssistance:
      "Need immediate assistance? Our Discord community is active 24/7 with helpful members and support staff.",
  },

  // Community Success
  community: {
    reasonSpam: "Spam or scam",
    reasonHarassment: "Harassment or hate",
    reasonUnsafe: "Unsafe or inappropriate",
    reasonOther: "Something else",
    justNow: "just now",
    minutesAgo: "{{count}}m ago",
    hoursAgo: "{{count}}h ago",
    daysAgo: "{{count}}d ago",
    confirmSaveFailed: "We couldn't save that confirmation. Please try again.",
    gateTitle: "One quick thing",
    gateBody: "Community Success is where members share their finds. Confirm you're 18+ to join the feed — we only ask once.",
    saving: "Saving…",
    confirmAdult: "I'm 18+",
    postLive: "Your success is live—nice find!",
    publishFailed: "We couldn't publish that post.",
    composerA11y: "Share a community success",
    composerTitle: "Share a success",
    composerSubtitle: "Add photos and tell us what you scored.",
    closeComposer: "Close composer",
    photosLabel: "Success photos",
    changePhotos: "Change photos · {{count}}/5",
    addPhotos: "Add 1–5 photos of your win",
    photoFormats: "JPEG, PNG, or GIF · 5 MB each",
    firstPhotoAlt: "First selected community success",
    selectedPhotoAlt: "Selected photo {{index}}",
    removePhoto: "Remove photo {{index}}",
    scoreLabel: "What did you score?",
    scorePlaceholder: "What did you find, where, and for how much?",
    guidelinesPrefix: "I agree to the",
    guidelinesLink: "Community Guidelines and Terms",
    guidelinesSuffix: ". All posts are reviewed.",
    reviewingPost: "Reviewing your post…",
    submit: "Share success",
    reportContent: "Report this content",
    reportMemberInstead: "Report member instead",
    photoAlt: "Community member's DealHawk purchase success, photo {{index}} of {{total}}",
    previousPhoto: "Previous photo",
    nextPhoto: "Next photo",
    showPhoto: "Show photo {{index}}",
    photoDialog: "Success photo",
    closePhoto: "Close photo",
    reportThanks: "Thanks—our team will review this.",
    reportFailed: "We couldn't submit that report.",
    reportMemberThanks: "Thanks—our team will review this member report.",
    memberContentHidden: "That member's content is now hidden for you.",
    memberPostsHidden: "That member's posts are now hidden for you.",
    blockFailed: "We couldn't block that member.",
    deleteCommentConfirm: "Delete this comment? This can't be undone.",
    commentRemoved: "Comment removed.",
    deleteCommentFailed: "We couldn't delete that comment.",
    postCommentFailed: "We couldn't post that comment.",
    likeFailed: "We couldn't update that like.",
    deletePostConfirm: "Delete this post? This can't be undone.",
    postRemoved: "Post removed.",
    deletePostFailed: "We couldn't delete that post.",
    comments: "Comments",
    closeComments: "Close comments",
    commentsSubtitle: "Be kind and keep it helpful.",
    commentOptions: "Comment options",
    deleteComment: "Delete comment",
    reportComment: "Report comment",
    reportMember: "Report member",
    blockMember: "Block member",
    startConversation: "Start the conversation",
    noCommentsYet: "No comments yet",
    startConversationHint: "Ask a helpful question or celebrate their win.",
    upgradeToCelebrate: "Upgrade to be the first to celebrate this win.",
    commentGuidelinesPrefix: "I agree to the",
    commentGuidelinesLink: "Community Guidelines",
    commentGuidelinesSuffix: ".",
    commentPlaceholder: "Add a kind comment…",
    postComment: "Post comment",
    upgradeToComment: "Upgrade to comment",
    memberBadge: "DealHawk member",
    newBadge: "New",
    sourceDiscord: "Discord",
    postOptions: "Post options",
    deletePost: "Delete post",
    reportPost: "Report post",
    unlikePost: "Unlike post",
    likePost: "Like post",
    loadFailed: "We couldn't load Community Success. Please try again.",
    title: "Community wins",
    newCount: "{{count}} new",
    subtitle: "What members are scoring",
    shareWin: "Share",
    upgradeToPost: "Upgrade to post",
    emptyTitle: "Be the first success story",
    emptyTitleLocked: "See a win? Upgrade to share yours",
    emptyText: "Found a win with DealHawk? Share the photo that proves it.",
    emptyTextLocked: "Free members can browse Community Success. Upgrade to post your finds.",
    emptyButton: "Share a success",
    upgradeToShare: "Upgrade to share",
    loadingMore: "Loading more…",
    footerNote: "Community Success is for purchases you made with DealHawk—not promotions, resale, or personal information.",
    navLabel: "Community Success",
    navNewWins: "{{count}} new community wins",
  },

  // Setup
  setup: {
    title: "Setup",
    description: "Complete your DealHawk setup",
    step: "Step",
    of: "of",
    chooseLanguage: "Choose Language",
    chooseLanguageSubtitle: "Select your preferred language to continue.",
    selectLanguage: "Select Language",
    languageDescription: "Choose your preferred language for the application",
    chooseWalmartCategories: "Choose Walmart Categories",
    chooseHomeDepotCategories: "Choose HD Categories",
    searchCategories: "Search categories...",
    failedToLoadCategories: "Failed to load categories",
    noCategoriesAvailable:
      "No {{retailerName}} categories available. Please try again later.",
    noCategoriesMatchSearch:
      "No categories match your search criteria. Try a different search term.",
    categoriesSelectedCount: "{{count}} of {{total}} categories selected",
    freeTrialTitle: "Start Your Subscription Today",
    freeTrialDescription:
      "Start finding profitable deals immediately. No long-term contracts.",
    fullAccessFeatures: "Full access to all features",
    cancelAnytime: "Cancel anytime",
    choosePlan: "Choose Plan",
    selectStores: "Select Stores",
    selectCategories: "Select Categories",
    complete: "Complete",
    getStarted: "Get Started",
    startFreeTrial: "Get Started",
    selectPlanDescription: "Choose the plan that best fits your needs",
    selectStoresDescription: "Select the stores you want to monitor",
    selectCategoriesDescription:
      "Choose the product categories you're interested in",
    completeSetup: "Complete Setup",
    noCreditCard: "No credit card required",
    choosePlanSubtitle: "Start with a plan that works for you.",
    chooseCategoriesSubtitle: "Select the categories you want to monitor.",
    chooseStoresSubtitle: "Select the stores you want to track.",
    basic: "Basic",
    starter: "Starter",
    pro: "Pro",
    enterprise: "Enterprise",
    popular: "Popular",
    discordAccess: "Discord Access",
    standardMonitoring: "Standard monitoring",
    advancedMonitoring: "Advanced monitoring",
    premiumMonitoring: "Premium monitoring",
    prioritySupport: "Priority support",
    dedicatedSupport: "24/7 dedicated support",
    allPlansIncludeTrial:
      "All plans include full access • Need more capabilities for your business?",
    contactEnterprise: "Contact us for custom enterprise solutions",
    chooseWhatToSee: "Choose what you want to see",
    addCategories: "+ Add Categories",
    noCategoriesSelected: "No categories selected",
    noCategoriesMessage:
      'No categories selected. Click "Add Categories" to get started.',
    chooseYourStores: "Choose your stores",
    noStoresSelected: "No stores selected",
    noStoresMessage:
      'No stores selected. Click "Select Stores" to get started.',
    skipForNow: "Skip for now",
    errorPlan: "Error with selected plan. Please try again.",
    errorSetup: "Error finishing setup. Please try again.",
    storeText: "Store",
    stores: "Stores",
    allCategories: "All Categories",
    selectedStores: "Selected Stores",
    addStores: "+ Add Stores",
    select: "Select",
    address: "Address",
    zipCode: "Zip Code",
    yourZipCode: "Your zip code",
    zipCodePlaceholder: "Enter 5-digit zip code",
    invalidZipCode: "Please enter a valid 5-digit zip code",
    validZipCode: "Valid zip code",
    invalidZipCodeError:
      "Please enter a valid 5-digit zip code before continuing.",
    storeId: "Store ID",
    starting: "Starting...",
    continue: "Continue",
    needHelp: "Need help?",
    contactTeam: "Contact our team",
    allPlansIncludeAccess:
      "All plans include full access to our monitoring tools",
    buildYourPlan: "Build Your Plan",
    trialMode: "Standard Mode:",
    trialModeDescription:
      "You can only add compatible plans. Remove incompatible plans to change selection.",
    paidMode: "💳 Paid Mode:",
    paidModeDescription:
      "You can only add other paid plans. Remove paid plans to add other plans.",
    chooseYourPlan: "Choose your plan",
    noPlanSelected: "No plan selected",
    storesPlural: "Stores",
    perMonthShort: "/mo",
    threeDayTrial: "",
    noTrialPayNow: "Pay now",
    trialOnlyWithTrials: "Compatible only with other standard plans",
    noTrialsWithPaid: "Cannot mix plan types",
    discord: "Discord",
    alwaysIncluded: "Always included",
    monthlyTotal: "Monthly Total",
  },

  // Extension
  extension: {
    title: "Chrome Extension",
    description:
      "Search for deals directly from any website with our Chrome extension.",
    downloadExtension: "Download Extension",
    downloadDescription:
      "Install the DealHawk Chrome extension to search for products and compare prices while browsing any shopping website.",
    visitChromeStore: "Visit Chrome Web Store",
    comingSoon: "Extension coming soon to the Chrome Web Store",
    apiKeySetup: "API Key Setup",
    apiKeyDescription:
      "The extension requires an API key to connect to your account. Generate one below and enter it in the extension settings.",
    yourApiKey: "Your API Key",
    active: "Active",
    hideApiKey: "Hide API key",
    showApiKey: "Show API key",
    copyToClipboard: "Copy to clipboard",
    copiedToClipboard: "✓ Copied to clipboard!",
    generateNewKey: "Generate New Key",
    regenerateKey: "Regenerate Key",
    deactivateKey: "Deactivate Key",
    created: "Created",
    lastUsed: "Last used",
    never: "Never",
    generateApiKey: "Generate API Key",
    noApiKey: "No API Key",
    noApiKeyDescription:
      "You don't have an active API key yet. Generate one to start using the Chrome extension.",
    generateFirst: "Generate Your First API Key",
    confirmDeactivate:
      "Are you sure you want to deactivate your API key? This will revoke access for the Chrome extension.",
    keyDeactivated: "API key has been deactivated successfully.",
    generateFailed: "Failed to generate API key. Please try again.",
    deactivateFailed: "Failed to deactivate API key. Please try again.",
    copyFailed:
      "Failed to copy to clipboard. Please select and copy the API key manually.",
    deactivating: "Deactivating...",
    noApiKeyFound: "No API Key Found",
    generateApiKeyDescription:
      "Generate an API key to connect the extension to your account.",
    generating: "Generating...",
    setupInstructions: "Setup Instructions",
    instruction1:
      "Download and install the DealHawk Chrome extension from the Web Store",
    instruction2: "Generate an API key using the section above",
    instruction3: "Copy your API key to clipboard",
    instruction4: "Open the extension and paste your API key in the settings",
    instruction5: "Start searching for products directly from any webpage",
  },

  // Favorites
  favorites: {
    title: "Saved products",
    description: "Products you've saved for easy access",
    noFavorites: "No saved products yet",
    noFavoritesMessage: "Start saving products to see them here.",
    addToFavorites: "Save",
    removeFromFavorites: "Unsave",
    viewProduct: "View Product",
    browseProducts: "Browse Products",
    loadingFavorites: "Loading your saved products...",
    failedToLoadFavorites: "Failed to load saved products",
    favoriteProductsFromStores: "saved products from your selected stores",
    favoriteProductFromStores: "saved product from your selected stores",
    tableHeaders: {
      product: "Product",
      bestPrice: "Best Price",
      msrp: "MSRP",
      stockStatus: "Stock Status",
      actions: "Actions",
    },
    noPricing: "No pricing",
    percentOff: "% OFF",
    inStock: "In Stock",
    outOfStock: "Out of Stock",
    stores: "stores",
    removeFavoriteTooltip: "Remove from saved products",
    loadingMore: "Loading more...",
    loadMore: "Load More",
    exportOptions: "Export Options",
    downloadCSV: "Download CSV",
  },

  // Alerts
  tracking: {
    title: "Product Alerts",
    description:
      "Set alerts for price changes and availability for your favorite products",
    noTracking: "No products with alerts",
    noTrackingMessage:
      "Start setting alerts on products to monitor their prices and availability.",
    addTracking: "Add Alert",
    removeTracking: "Remove Alert",
    priceAlert: "Price Alert",
    priceDropAlert: "Price Drop Alert",
    backInStock: "Back in Stock",
    // New translations for TracksPage
    loadingUserData: "Loading user data...",
    loadingTracking: "Loading alerts...",
    errorLoadingTracking: "Error loading alerts",
    failedToLoadTracking: "Failed to load alerts",
    tryAgain: "Try Again",
    trackingCount: "alerts",
    noTrackedProducts: "No products with alerts",
    noTrackedProductsMessage:
      "Start setting alerts on products by clicking the alert button on any product in the Products page",
    trackedProducts: "Products with Alerts",
    howItWorksDescription:
      "Set alerts on products for price changes and get Discord notifications when they reach your target price. Products are checked periodically across all the stores you have configured, and you'll be alerted when deals are found. You can set alerts on up to {{limit}} products at a time.",
    configureNotifications: "Configure your notification preferences in",
    settings: "Settings",
    upcLabel: "UPC:",
    currentPrice: "Current Price",
    targetPrice: "Target Price",
    potentialSavings: "Potential Savings",
    targetReached: "Target reached!",
    trackingStatus: "Alert Active...",
    lastChecked: "Last checked:",
    minutesAgo: "{{minutes}} minutes ago",
    hoursAgo: "{{hours}} hours ago",
    daysAgo: "{{days}} days ago",
    manuallyUpdateTooltip: "Manually update product data",
    removeTrackingTooltip: "Remove alert",
    update: "Update",
    remove: "Remove",
    exportOptions: "Export Options",
    downloadCSV: "Download CSV",
  },

  // Configuration
  config: {
    title: "Configuration",
    description: "Configure your deal hunting preferences",
    filters: "Filters",
    stores: "Stores",
    categories: "Categories",
    searchTerms: "Search Terms",
    priceRange: "Price Range",
    discountThreshold: "Discount Threshold",
    saveConfiguration: "Save Configuration",
  },

  // Notifications
  notifications: {
    success: "Success",
    error: "Error",
    warning: "Warning",
    info: "Info",
    saved: "Saved successfully",
    deleted: "Deleted successfully",
    updated: "Updated successfully",
    created: "Created successfully",
    failed: "Operation failed",
    tryAgain: "Please try again",
  },

  // Errors
  errors: {
    genericError: "Something went wrong. Please try again.",
    networkError: "Network error. Please check your connection.",
    serverError: "Server error. Please try again later.",
    notFound: "Not found",
    unauthorized: "Unauthorized access",
    forbidden: "Access forbidden",
    validationError: "Please check your input",
    emailRequired: "Email is required",
    passwordRequired: "Password is required",
    nameRequired: "Name is required",
    passwordsNoMatch: "Passwords do not match",
    passwordTooShort: "Password must be at least 6 characters",
    invalidEmail: "Please enter a valid email address",
    emailAlreadyExists: "This email is already registered",
    userNotFound: "User not found",
    incorrectPassword: "Incorrect password",
    pageNotFound: "Page Not Found",
    pageNotFoundMessage:
      "Oops! The page you're looking for doesn't exist. It might have been moved, deleted, or you entered the wrong URL.",
    goHome: "Go Home",
    goBack: "Go Back",
    needHelp: "Need help? Try these:",
    helpCenter: "Help Center",
  },

  // Bot Filters
  botFilters: {
    title: "Sourcing Filters",
    description: "Manage how we find deals for you",
    chooseCategories: "Choose Categories",
    chooseCategoriesToMonitor: "Which categories are you interested in?",
    searchCategories: "Search categories...",
    selectAll: "Select All",
    clearAll: "Clear All",
    categoriesSelected: "{{count}} of {{total}} categories selected",
    shown: "{{count}} shown",
    noCategoriesFound: "No categories found",
    tryAdjustingSearchTerms: "Try adjusting your search terms",
    youHaveUnsavedChanges: "You have unsaved changes",
    saving: "Saving...",
    saveChanges: "Save Changes",
    botConfiguration: "Sourcing Filters",
    manageProductCategories:
      "Manage the product categories DealHawk watches for deals",
    categoriesSelectedOf: "{{selected}} of {{total}} categories selected",
    searchTermsAdded: "{{count}} search terms added",
    editCategories: "Edit Categories",
    changesAreSavedAutomatically: "Changes are saved automatically",
    selectedCategories: "Selected Categories",
    noCategoriesSelectedTitle: "No categories selected",
    noCategoriesSelectedMessage:
      'Click "Edit Categories" to start monitoring deals',
    clickEditCategoriesToStart:
      'Click "Edit Categories" to start monitoring deals',
    addCategories: "Add Categories",
    searchTerms: "Search Terms",
    addSpecificKeywords:
      "Add specific keywords to find deals beyond categories",
    addKeywordsToFindSpecific:
      "Add keywords to find specific products beyond your selected categories",
    searchTermsUsed: "{{used}} of {{limit}} search terms used",
    plan: "{{plan}} plan",
    enterSearchTerm: "Enter a search term",
    searchTermPlaceholder:
      "Enter a search term (e.g., 'PlayStation 5', 'iPhone', 'Nike')",
    noSearchTermsTitle: "No search terms added",
    noSearchTermsMessage:
      "Add search terms to find deals for specific products or brands",
    addSearchTermsDescription:
      "Add search terms to find deals for specific products or brands",
    removeSearchTerm: "Remove search term",
    loadingCategories: "Loading categories...",
    pleaseLoginToAccess: "Please log in to access sourcing filters.",
    failedToLoadCategories: "Failed to load categories. Please try again.",
    configurationSaved: "Configuration saved!",
    configurationSavedMessage:
      "{{categoryCount}} {{categoryText}} and {{searchTermCount}} search {{searchTermText}} selected.",
    failedToSaveBotFilters: "Failed to save sourcing filters. Please try again.",
    searchTermAlreadyExists: "This search term already exists",
    searchTermLimitReached:
      "You can only have {{limit}} search terms on the {{plan}} plan",
    savedJustNow: "Saved just now",
    savedMinutesAgo: "Saved {{minutes}} minutes ago",
    savedAt: "Saved at {{time}}",
    stores: "Stores",
    addStoresToMonitor: "Select stores to monitor for deals",
    addStoresToFindSpecific:
      "Add stores to monitor specific locations for deals and clearance items",
    storesUsed: "{{used}} of {{limit}} stores used",
    storesSelectedOf: "{{selected}} of {{limit}} stores selected",
    addStores: "Add Stores",
    noStoresSelectedTitle: "No stores selected",
    extraStoresTitle: "Extra stores",
    extraStoresDescription:
      "Your plan includes {{count}} extra stores near the ones you picked. We check them for the deals found at stores nearby, and their deals show up in All Stores. You can also switch to one anytime.",
    extraStoresDescriptionWalmart:
      "Your plan includes {{count}} extra stores near the ones you picked. They get a lighter daily scan of clearance, rollback and reduced-price items, and their deals show up in All Stores. You can also switch to one anytime.",
    extraStoresView: "View deals",
    noStoresSelectedMessage:
      "Add stores to monitor deals at specific locations",
    removeStore: "Remove store",
    storeMaxReached: "Store limit reached",
    contactDiscordToUpgrade:
      "Contact us on Discord to upgrade your plan for more stores",
    storeLimitReached:
      "You can only have {{limit}} stores on the {{plan}} plan",
    setupCategories: "Select Categories",
    setupStores: "Select Stores",
    pleaseSelectAtLeastOneCategory:
      "Please select at least one category to continue",
    pleaseSelectAtLeastOneStore: "Please select at least one store to continue",
    pleaseCompleteAllSteps:
      "Please complete all steps before finishing setup",
    completeSetup: "Complete Setup",
    forRetailer: "for {{retailer}}",
  },

  // Add Categories Modal
  addCategoriesModal: {
    title: "Add Categories",
    searchPlaceholder: "Search categories...",
    loadingCategories: "Loading categories...",
    noCategoriesFound: "No categories found",
    noCategoriesAvailable: "No categories available",
    alreadyAdded: "Already added",
    categoriesSelected: "{{count}} {{categoryText}} selected",
    category: "category",
    categories: "categories",
    cancel: "Cancel",
    addSelected: "Add Selected",
  },

  // Add Stores Modal
  addStoresModal: {
    title: "Select Stores",
    planLimitDescription:
      "You can select up to {{limit}} stores with your current plan",
    searchZipPlaceholder:
      "Enter 5-digit zip code for nearby stores (50 mile radius)",
    searchGeneralPlaceholder: "Search by zip code, city, or address",
    nearbyStoresInfo:
      "Showing stores within {{radius}} miles of zip code {{zipCode}}, sorted by distance",
    limitReached: "Limit reached",
    previous: "Previous",
    next: "Next",
    showingStores: "Showing {{start}}-{{end}} of {{total}} stores",
    selectedCount: "({{selected}}/{{limit}} selected)",
    noStoresFound: "No stores found",
    noStoresFoundZip:
      "No stores found for this zip code. Try a different zip code.",
    noStoresFoundGeneral:
      "Try searching with a different term or enter a 5-digit zip code to find nearby stores",
    searchToGetStarted: "Search for stores to get started",
    searchInstructions:
      "Enter a 5-digit zip code to find stores within 50 miles, or search by city/address",
    storeCountSelected: "{{count}} of {{limit}} stores selected",
    storeCountSelectedSingular: "{{count}} of {{limit}} store selected",
    limitReachedWarning: "(Limit reached)",
    cancel: "Cancel",
    saveSelection: "Save Selection",
    address: "Address",
    zipCode: "Zip Code",
    storeId: "Store ID",
    milesAway: "{{distance}} miles away",
    zipLabel: "Zip:",
    storeIdLabel: "Store ID: #{{id}}",
    currentStore: "Current Store",
    cannotRemove: "Cannot remove",
    loadingStores: "Finding stores near {{zipCode}}...",
    geocodingInProgress: "Looking up coordinates and calculating distances",
  },

  // Support
  support: {
    videoTutorials: "Video tutorials",
    videoTutorialsDescription: "Short videos that show how to use every feature.",
    title: "Support Center",
    subtitle: "We're here to help you get the most out of DealHawk",
    emailSupport: "Email Support",
    available247: "Available 24/7",
    contactEmailText: "Get in touch with our support team:",
    fastResponse: "Fast Response",
    responseTime: "We typically respond within 24 hours",
    expertHelp: "Expert Help",
    expertHelpDesc: "Our team knows DealHawk inside and out",
    backToApp: "Back to App",
    searchPlaceholder: "Search support topics",
    searchAriaLabel: "Search support topics",
    clearSearchAriaLabel: "Clear search",
    contactUsButton: "Contact us",
    popularTitle: "Popular questions",
    popularSubtitle: "Quick answers organized by feature and page.",
    faq: {
      gettingStarted: {
        title: "Getting started",
        description: "Landing, sign up, setup, paywall, and success flows.",
        q1: "What is DealHawk and how does it work?",
        a1:
          "DealHawk monitors supported retailers and surfaces deals, inventory changes, and price drops so you can act fast.",
        q2: "How do I create an account?",
        a2: "Use the Register page to sign up with email or continue with Google or Discord.",
        q3: "Do I need to verify my email?",
        a3: "Yes. Check your inbox for a verification code and enter it on the Verification page.",
        q4: "How do I pick a plan during setup?",
        a4:
          "The Set Up and Paywall pages explain plan differences and let you choose stores before checkout.",
        q5: "Payment succeeded but I still cannot access features.",
        a5: "Open the Success page or refresh the app, then check Settings to confirm your plan.",
      },
      accountSecurity: {
        title: "Account and security",
        description: "Login, password reset, account deletion, and OAuth.",
        q1: "I forgot my password. What should I do?",
        a1: "Go to the Forgot Password page and request a reset code, then set a new password.",
        q2: "My reset code is not working.",
        a2: "Request a new code and make sure you enter the latest one on the Reset Password page.",
        q3: "Can I sign in with Google or Discord?",
        a3: "Yes. Use the Google or Discord buttons on Login or Register to authenticate.",
        q4: "How do I delete my account?",
        a4: "Open the Delete Account page and follow the confirmation steps.",
        q5: "I do not see a verification email.",
        a5: "Check spam or promotions, then resend the code from the Verification page.",
      },
      productSearchScanning: {
        title: "Product search and scanning",
        description: "Search, barcode scan, and store results.",
        q1: "What can I search for?",
        a1: "Search by product name, UPC, SKU, or paste a retailer URL on the Product Search page.",
        q2: "How do I scan a barcode or QR code?",
        a2: "Use the scan button on Product Search to open your camera and scan the code.",
        q3: "Why is my product not showing up?",
        a3: "Try another retailer, confirm the UPC/SKU, and make sure the store is supported.",
        q4: "Why does search take so long?",
        a4: "Some retailers take longer to respond. Wait up to 30 seconds and retry.",
        q5: "Can I review recent searches?",
        a5: "Yes. Product Search keeps a recent list and search history for quick access.",
      },
      productsFiltersCategories: {
        title: "Products, filters, and categories",
        description: "Products page filters, sorting, and store switching.",
        q1: "How do I filter products by store or category?",
        a1: "Use the store dropdown and category filters on the Products page.",
        q2: "How do I find only in-stock or price-drop items?",
        a2: "Use the status filters like in stock, new, price drop, or saved.",
        q3: "What are hot deals and penny items?",
        a3: "These toggles highlight aggressive discounts and items at minimal prices.",
        q4: "Can I sort products by price or last update?",
        a4: "Yes. Use the sort menu on Products to reorder results.",
        q5: "I cannot see any products after filtering.",
        a5: "Clear or reset filters, then try a broader category or store.",
      },
      productDetailsAlerts: {
        title: "Product details and alerts",
        description: "Product Detail, tracking, and alerts.",
        q1: "How do I set a price alert?",
        a1: "Open a product and tap Track or Alert, then set your target price.",
        q2: "What does Ready vs Watching mean on alerts?",
        a2: "Ready means your target price has been met. Watching means it is still above.",
        q3: "Can I manually refresh a tracker?",
        a3: "Yes. Use the refresh action in the new Alerts page to pull updated data.",
        q4: "How do I remove an alert?",
        a4: "Open your Alerts list and delete the tracker you no longer want.",
        q5: "Can I export my alerts?",
        a5: "The legacy Alerts page includes a CSV export for your tracker list.",
      },
      leadsDealDiscovery: {
        title: "Leads and deal discovery",
        description: "Leads page and deal actions.",
        q1: "What are leads?",
        a1: "Leads are curated deal opportunities with pricing and store context.",
        q2: "How do I save a lead?",
        a2: "Use the save action on a lead to add it to your favorites.",
        q3: "Can I set an alert from a lead?",
        a3: "Yes. Use the alert action to track price changes for that lead.",
        q4: "How do I check store stock from a lead?",
        a4: "Use the Check Stock button to view store availability.",
      },
      specialtyPages: {
        title: "Specialty pages",
        description: "Woot and Penny Items Pro.",
        q1: "What is Penny Items Pro?",
        a1: "It is a focused page for ultra-low priced items, often based on store rules.",
        q2: "What is the Woot page?",
        a2: "It highlights deals from Woot when available.",
      },
      settingsBillingNotifications: {
        title: "Settings, billing, and notifications",
        description: "Settings and sourcing filters.",
        q1: "How do I change or cancel my subscription?",
        a1: "Go to Settings and open Billing to manage your current plan.",
        q2: "How do I link Discord for notifications?",
        a2: "Open Settings and connect your Discord account in Notifications.",
        q3: "Why can I only select a limited number of stores?",
        a3: "Store limits depend on your plan and are configured in Sourcing Filters.",
      },
      helpContactLegal: {
        title: "Help, contact, and legal",
        description: "Help, Contact Us, Support, Terms, and Privacy.",
        q1: "How do I contact support?",
        a1: "Use the Support page email link or join the Discord server on Help.",
        q2: "Where can I submit a support ticket?",
        a2: "Join the Discord server and follow the Help page steps to open a ticket.",
        q3: "Where can I read the Terms or Privacy Policy?",
        a3: "Open the Terms of Service or Privacy Policy pages from the footer.",
        q4: "Why am I seeing a Not Found page?",
        a4: "The page does not exist or you used an outdated link. Go back to Home.",
      },
    },
  },

  // Success Page
  success: {
    paymentSuccessful: "Payment Successful!",
    thankYou: "Thank you for your purchase. Your account has been upgraded.",
    redirecting: "Redirecting to dashboard in",
    second: "second",
    seconds: "seconds",
    goToDashboard: "Go to Dashboard Now",
    supportMessage:
      "If you have any questions, please contact our support team.",
  },
  // Reseller landing page ("/")
  resellerLanding: {
    navLabel: "Main",
    navHow: "How it works",
    navHauls: "Hauls",
    navPricing: "Pricing",
    navFaq: "FAQ",
    logIn: "Log in",
    getStarted: "Start hunting",
    heroTitle: "Hunt clearance. Flip it for profit.",
    heroLede: "The clearance-hunting app for resellers. Every markdown at stores near you, how many are left on the shelf, and what it resells for on Amazon and eBay.",
    heroCta: "Start hunting",
    heroHauls: "See member hauls",
    heroStores: "Hunt Walmart, Target, Costco, Dollar General and big box stores",
    findsLabel: "Recent member hunts",
    find1: "15 vacuums · $0.01",
    find2: "9 coolers · $15",
    find3: "A cart of Milwaukee · $0.01",
    find4: "35 hoses · $0.01",
    find5: "“Took all 7” · 70% off",
    find6: "Griddles · half off",
    find7: "80 towels · $1",
    bentoLabel: "What DealHawk shows you",
    resaleTitle: "Know your profit before you buy",
    resaleText: "Real Amazon and eBay sales, after fees, on every find. Taking all 12? It does the math.",
    resaleAmazon: "Amazon +${{amount}}",
    resaleEbay: "eBay +${{amount}}",
    resaleExample: "Per unit, example",
    resaleAlt: "Item at $50 with Amazon and eBay profit per unit",
    alertsTitle: "First to the hunt",
    alertsBig: "Instant alerts",
    alertsText: "Your phone buzzes when a store marks something down.",
    stockLeft: "{{count}} left",
    stockTitle: "Hunt the whole shelf",
    stockText: "See how many are left and which aisle. Take them all.",
    pennyWas: "Was",
    pennyTitle: "Penny hunting",
    pennyText: "Whole shelves marked down to a cent. Pure profit.",
    feedTitle: "Your hunting grounds",
    feedText: "Every markdown at your stores, best first.",
    feedAlt: "Deals list",
    cartAlt: "Cart full of Milwaukee tools",
    cartCaption: "A cart of Milwaukee for $0.01 · member hunt",
    discordTitle: "Hunt with other resellers",
    discordText: "Members post what they found, where, and what it sold for.",
    stepsTitle: "How resellers hunt with DealHawk",
    step1Title: "Get the alert",
    step1Text: "Something near you just hit clearance.",
    step2Title: "Hunt it down",
    step2Text: "You know the aisle and how many are left.",
    step3Title: "Flip it",
    step3Text: "List it on eBay, Amazon or Marketplace.",
    step4Title: "Get paid",
    step4Text: "Bought at clearance, sold at retail. Keep the profit.",
    haulsTitle: "This month's best hunts",
    haul1Alt: "Vacuums",
    haul1Caption: "15 vacuums · $0.01 each",
    haul2Alt: "Coolers",
    haul2Caption: "9 coolers · $15 each",
    haul3Alt: "Hoses",
    haul3Caption: "35 boxes of hoses · $0.01",
    haul4Alt: "Stack of Carote cookware sets on a store shelf",
    haul4Caption: "“70% off. I took all 7.”",
    faqTitle: "Resellers ask",
    faq1Q: "Do I need experience?",
    faq1A: "No. Start hunting at one store. The app shows what's worth flipping and what you'll make.",
    faq2Q: "How much can I make?",
    faq2A: "Depends on what you hunt down and how much you buy. Most members say one good flip pays for the month.",
    faq3Q: "Where do resellers sell?",
    faq3A: "eBay, Amazon, Facebook Marketplace, local groups.",
    pricingTitle: "One good hunt pays for the month",
    planBasic: "1 home store + 4 nearby extra, at Walmart and a Big Box Retailer",
    planPlus: "Up to 3 home stores + 7 extra at Walmart and Big Box, 9 chains, penny items",
    planPro: "Up to 5 home stores + 15 extra at Walmart (10 at Big Box), plus what every item resells for",
    pricingExplainer: "Home stores are the ones you pick. Extra stores are the closest Walmart and Big Box stores around them, added free with a lighter daily scan for clearance and markdowns.",
    pricingNote: "Per month. 50% off your first month.",
    closingTitle: "See what's on clearance near you.",
    examplePrices: "Example prices in app screens.",
    footerLabel: "Footer",
    languageLabel: "Language",
  },
};
